import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

import BlueButton from "../../../Component/Common/BlueButton";
import SelectField from "../../../Component/RetailerComponents/SelectField";
import InputField from "../../../Component/RetailerComponents/InputField";
import MultiSelectChips from "../../../Component/RetailerComponents/MultiSelectChips";
import { BASE_URL } from "../../../config/api";
import toast from "react-hot-toast";
import SearchableMultiSelect from "./SearchableMultiSelect";


function CategoryGuard({
  blocked,
  tooltip = "Please select Category first",
  children,
}) {
  return (
    <div className="relative group">
      {/* TOOLTIP */}
      {blocked && (
        <div className="
          pointer-events-none
          absolute -top-9 left-1/2 -translate-x-1/2
          whitespace-nowrap
          rounded-md bg-slate-900 px-3 py-1
          text-xs text-white
          opacity-0 group-hover:opacity-100
          transition
          z-20
        ">
          {tooltip}
          <span className="
            absolute left-1/2 top-full -translate-x-1/2
            border-4 border-transparent border-t-slate-900
          " />
        </div>
      )}

      {/* CLICK / HOVER BLOCKER */}
      {blocked && (
        <div className="absolute inset-0 z-10 cursor-not-allowed" />
      )}

      {/* FIELD */}
      <div className={blocked ? "opacity-60" : ""}>
        {children}
      </div>
    </div>
  );
}


export default function EmployeeForm({ managers: parentManagers, employee, onSubmit, onClose }) {

  const [managers, setManagers] = useState(parentManagers || []);
  const navigate = useNavigate();
  
  const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);
  const [selectedDistrictText, setSelectedDistrictText] = useState("");
  const [loadingPin, setLoadingPin] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  // const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [managerStates, setManagerStates] = useState([]);
  const [isManagerDistrictLocked, setIsManagerDistrictLocked] = useState(false);
  const [managerDistricts, setManagerDistricts] = useState([]);
  const [managerLocationMode, setManagerLocationMode] = useState(null);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isEdit = Boolean(employee?.id);
  const initialFormState = {
    employee_type: "",
    position: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    phone_personal: "",
    phone_official: "",
    official_email: "",
    employee_id: "",
    photo: null,

    category: [],
    user_type: "",
    handle: [],

    report_to: "",
    location_mode: "state",
    selected_state: "",
    locations: [],
    pinned_state: "",
    pinned_district: "",
    // password: 123456,
  };

  const [form, setForm] = useState(initialFormState);
  const isCategorySelected = form.category.length > 0;
  const isManager = form.position === "MANAGER";

  const sessionUser = JSON.parse(localStorage.getItem("authUserData"));
  const companyId = sessionUser?.id;

useEffect(() => {
  if (!employee) return;

  const position =
    employee.position?.toUpperCase() === "MANAGER"
      ? "MANAGER"
      : "EXECUTIVE";

  const locationMode = employee.location_mode || "state";

  setForm(prev => ({
    ...prev,

    employee_type: employee.employee_type
      ? employee.employee_type.toLowerCase()
      : "",
    position: employee.position
      ? employee.position.toUpperCase()
      : "",

    first_name: employee.first_name || "",
    middle_name: employee.middle_name || "",
    last_name: employee.last_name || "",

    phone_personal: employee.personal_phone || "",
    phone_official: employee.official_phone || "",
    official_email: employee.official_email || "",

    user_type: employee.type_of_user || "",
    report_to: Number(employee.reports_to) || "",

    category: employee.categories || [],
    handle: employee.handle || [],

    location_mode: locationMode,

    selected_state:
      locationMode === "district" || locationMode === "state"
        ? employee.state || ""
        : "",

    locations:
      locationMode === "district"
        ? employee.district
          ?.split(",")
          .map(d => d.trim().toUpperCase()) || []
        : locationMode === "state"
        ? employee.state?.split(",").map(s => s.trim()) || []
        : employee.pincodes?.map(p => ({
            pincode: p,
            state: employee.state,
            district: employee.district,
          })) || [],
  }));

  setPhotoUrl(employee.photo_url || "");
}, [employee]);


  useEffect(() => {
    if (!isEdit) return;
    if (!employee?.reports_to) return;
    if (managers.length === 0) return;

    setForm(prev => ({
      ...prev,
      report_to: Number(employee.reports_to),
    }));
  }, [managers, isEdit, employee]);


  const handleImageUpload = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  setUploadLoading(true);

  const formData = new FormData();
  formData.append("file", file);
  formData.append("tag", "Employee");

  try {
    const res = await fetch(`${BASE_URL}warranty/upload-file`, {
      method: "POST",
      body: formData,
    });

    const data = await res.json();

    if (data?.data?.file_url) {
      setPhotoUrl(data.data.file_url);
      toast.success("Image uploaded");
    }
  } catch {
    toast.error("Image upload failed");
  } finally {
    setUploadLoading(false);
  }
};

  const [errors, setErrors] = useState({});
  const [districtList, setDistrictList] = useState([]);

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "position" && value === "MANAGER"
        ? { report_to: "" }
        : {}),
    }));
  };

   function toggleMultiSelect(name, value) {
    setForm(prev => {
      const list = new Set(prev[name]);
      list.has(value) ? list.delete(value) : list.add(value);
      return { ...prev, [name]: [...list] };
    });
  }

  /* ---------------- STATES ---------------- */
  const stateList = [
    { code: "AP", state: "ANDHRA PRADESH" },
    { code: "TG", state: "TELANGANA" },
    { code: "AS", state: "ASSAM" },
    { code: "BR", state: "BIHAR" },
    { code: "CT", state: "CHHATTISGARH" },
    { code: "DL", state: "DELHI" },
    { code: "GA", state: "GOA" },
    { code: "GJ", state: "GUJARAT" },
    { code: "HR", state: "HARYANA" },
    { code: "HP", state: "HIMACHAL PRADESH" },
    { code: "JK", state: "JAMMU AND KASHMIR" },
    { code: "JH", state: "JHARKHAND" },
    { code: "KA", state: "KARNATAKA" },
    { code: "KL", state: "KERALA" },
    { code: "MP", state: "MADHYA PRADESH" },
    { code: "MH", state: "MAHARASHTRA" },
    { code: "OD", state: "ODISHA" },
    { code: "PB", state: "PUNJAB" },
    { code: "RJ", state: "RAJASTHAN" },
    { code: "TN", state: "TAMIL NADU" },
    { code: "UP", state: "UTTAR PRADESH" },
    { code: "UK", state: "UTTARAKHAND" },
    { code: "WB", state: "WEST BENGAL" },
  ];

  const HANDLES = ["AGENT", "RETAILER", "END_USER"];

  useEffect(() => {
    async function fetchCategories() {
      try {
        setLoadingCategories(true);
        const res = await fetch(`${BASE_URL}warranty/get-categories`);
        const data = await res.json();
        if (data) {
          const active = data.filter(cat => cat.status === 1);
          setCategories(active);
        }
      } catch (error) {
        console.error("Category fetch error", error);
        toast.error("Failed to load categories");
      } finally {
        setLoadingCategories(false);
      }
    }
    fetchCategories();
  }, []);

  useEffect(() => {
  if (!isCategorySelected) return;
  if (!form.user_type) return;

  async function fetchManagers() {
    try {
      setLoadingManagers(true);

      const res = await axios.post(
        `${BASE_URL}company/employee/all`,
        {
          company_id: companyId,
          type_of_user: form.user_type, // 🔥 Sales / Operation
          position: "MANAGER",
        }
      );

      if (res.data?.status) {
        setManagers(res.data.data?.data || []);
      }
    } catch (err) {
      toast.error("Failed to load managers");
    } finally {
      setLoadingManagers(false);
    }
  }

  fetchManagers();
}, [form.user_type, isCategorySelected]);


useEffect(() => {
  if (isEdit) return; // 🔥 THIS LINE FIXES IT
  if (isCategorySelected) return;

  setForm(prev => ({
    ...prev,
    user_type: "",
    position: "",
    report_to: "",
    handle: [],
  }));

  setManagers([]);
}, [isCategorySelected, isEdit]);


//   useEffect(() => {
//   if (form.position !== "EXECUTIVE") return;

//   async function fetchManagers() {
//     try {
//       setLoadingManagers(true);

//       const sessionUser = JSON.parse(localStorage.getItem("authUserData"));
//       const companyId = sessionUser?.id;

//       const res = await axios.post(
//         `${BASE_URL}company/employee/all?position=MANAGER`,
//         { company_id: companyId }
//       );

//       if (res.data?.status) {
//         // ✅ ALWAYS ARRAY
//         setManagers(res.data.data?.data || []);
//       }
//     } catch (err) {
//       toast.error("Failed to load managers");
//     } finally {
//       setLoadingManagers(false);
//     }
//   }

//   fetchManagers();
// }, [form.position]);

  const resetManagerDerivedData = () => {
    setManagerStates([]);
    setDistrictList([]);

    setForm(prev => ({
      ...prev,
      location_mode: "state",
      selected_state: "",
      locations: [],
    }));
  };

//   const handleManagerSelect = (managerId) => {
//   const manager = managers.find(m => m.id === Number(managerId));
//   if (!manager) return;

//   // 🔥 AUTO HANDLE INHERIT
//   const managerHandles = Array.isArray(manager.handle)
//     ? manager.handle
//     : [];

//   // reset everything first
//   setManagerStates([]);
//   setManagerDistricts([]);
//   setDistrictList([]);

//   setManagerLocationMode(manager.location_mode);

//   setForm(prev => ({
//     ...prev,
//     report_to: manager.id,
//     handle: managerHandles, // ✅ AUTO SET HANDLE
//   }));

//   // ---------- STATE MODE ----------
//   if (manager.location_mode === "state") {
//     const states = manager.state
//       ? manager.state.split(",").map(s => s.trim().toLowerCase())
//       : [];

//     setManagerStates(states);

//     setForm(prev => ({
//       ...prev,
//       location_mode: "district",
//       selected_state: "",
//       locations: [],
//     }));

//     return;
//   }

//   // ---------- DISTRICT MODE ----------
//   if (manager.location_mode === "district") {
//     const districts = manager.district
//       ? manager.district.split(",").map(d => d.trim().toUpperCase())
//       : [];

//     setManagerStates([manager.state.toLowerCase()]);
//     setManagerDistricts(districts);

//     // setForm(prev => ({
//     //   ...prev,
//     //   location_mode: "district",
//     //   selected_state: manager.state,
//     //   locations: districts,
//     // }));
//     setForm(prev => ({
//       ...prev,
//       location_mode: "district",
//       selected_state: manager.state,
//       locations: isEdit
//         ? employee?.district
//             ?.split(",")
//             .map(d => d.trim().toUpperCase()) || []
//         : districts,
//     }));


//     fetchDistricts(manager.state);
//   }
// };


// const handleManagerSelect = (managerId) => {
//   const manager = managers.find(m => m.id === Number(managerId));
//   if (!manager) return;

//   const managerHandles = Array.isArray(manager.handle)
//     ? manager.handle
//     : [];

//   const executiveDistricts = employee?.district
//     ? employee.district.split(",").map(d => d.trim().toUpperCase())
//     : [];

//   setManagerLocationMode(manager.location_mode);

//   setForm(prev => ({
//     ...prev,
//     report_to: manager.id,
//     handle: managerHandles,
//     location_mode: "district",
//     selected_state: manager.state,
//   }));

//   fetchDistricts(manager.state).then(() => {
//     if (manager.location_mode === "state") {
//       // 🔥 SHOW ALL DISTRICTS OF STATE
//       setManagerDistricts([]); // no restriction
//       setForm(prev => ({
//         ...prev,
//         locations: isEdit ? executiveDistricts : [],
//       }));
//     }

//     if (manager.location_mode === "district") {
//       const managerDistrictsList = manager.district
//         ? manager.district.split(",").map(d => d.trim().toUpperCase())
//         : [];

//       setManagerDistricts(managerDistrictsList);

//       // 🔥 Only allow intersection
//       const allowedExecutiveDistricts = executiveDistricts.filter(d =>
//         managerDistrictsList.includes(d)
//       );

//       setForm(prev => ({
//         ...prev,
//         locations: isEdit ? allowedExecutiveDistricts : managerDistrictsList,
//       }));
//     }
//   });
// };


const handleManagerSelect = (managerId) => {
  const manager = managers.find(m => m.id === Number(managerId));
  if (!manager) return;

  const managerHandles = Array.isArray(manager.handle)
    ? manager.handle
    : [];

  const executiveDistricts = employee?.district
    ? employee.district.split(",").map(d => d.trim().toUpperCase())
    : [];

  // ✅ SET MANAGER STATES
  const states = manager.state
    ? manager.state.split(",").map(s => s.trim().toLowerCase())
    : [];

  setManagerStates(states);

  setManagerLocationMode(manager.location_mode);

  setForm(prev => ({
    ...prev,
    report_to: manager.id,
    handle: managerHandles,
    location_mode: "district",
    selected_state: manager.state,
  }));

  fetchDistricts(manager.state).then(() => {

    if (manager.location_mode === "state") {
      setManagerDistricts([]);

      setForm(prev => ({
        ...prev,
        locations: isEdit ? executiveDistricts : [],
      }));
    }

    if (manager.location_mode === "district") {

      const managerDistrictsList = manager.district
        ? manager.district.split(",").map(d => d.trim().toUpperCase())
        : [];

      setManagerDistricts(managerDistrictsList);

      const allowedExecutiveDistricts = executiveDistricts.filter(d =>
        managerDistrictsList.includes(d)
      );

      setForm(prev => ({
        ...prev,
        locations: isEdit ? allowedExecutiveDistricts : managerDistrictsList,
      }));
    }

  });
};

  /* ---------------- FETCH DISTRICTS ---------------- */
  // const fetchDistricts = async (stateName) => {
  //   try {
  //     setLoadingDistricts(true);      // 🔥 start loader
  //     setDistrictList([]);

  //     const res = await axios.get(
  //       `https://goelectronix.in/api/v1/org_user/statedistrict?state=${stateName}`
  //     );

  //     setDistrictList(res.data || []);
  //   } catch {
  //     Swal.fire("Error", "Failed to fetch districts", "error");
  //   } finally {
  //     setLoadingDistricts(false);     // 🔥 stop loader
  //   }
  // };

  const fetchDistricts = async (stateName) => {
  try {
    setLoadingDistricts(true);
    setDistrictList([]);

    const res = await axios.get(
      `https://goelectronix.in/api/v1/org_user/statedistrict?state=${stateName}`
    );

    setDistrictList(res.data || []);
    return res.data;
  } catch {
    Swal.fire("Error", "Failed to fetch districts", "error");
    return [];
  } finally {
    setLoadingDistricts(false);
  }
};


  // const visibleDistricts =
  // managerLocationMode === "district"
  //   ? districtList
  //       .map(d => d.district)
  //       .filter(d => managerDistricts.includes(d))
  //   : districtList.map(d => d.district);

  const visibleDistricts =
  managerLocationMode === "district" && managerDistricts.length > 0
    ? districtList
        .map(d => d.district)
        .filter(d => managerDistricts.includes(d))
    : districtList.map(d => d.district);


  /* ---------------- PINCODE LOOKUP ---------------- */
 async function fetchFromPincode(pin) {
  const res = await axios.get(
    `https://goelectronix.in/public/api/v1/pincode/${pin}`
  );

  if (!res.data?.success) return;

  setForm((prev) => {
    const exists = prev.locations.some(
      (l) => l.pincode === pin
    );

    if (exists) {
      toast.error("Pincode already selected");
      return prev;
    }

    return {
      ...prev,
      locations: [
        ...prev.locations,
        {
          pincode: pin,
          state: res.data.state,
          district: res.data.district,
        },
      ],
      pincode: "",
    };
  });
}

  useEffect(() => {
    if (!isEdit) return;
    if (form.location_mode !== "district") return;
    if (!form.selected_state) return;

    fetchDistricts(form.selected_state);
  }, [isEdit, form.location_mode, form.selected_state]);


  const validateForm = () => {
  const newErrors = {};

  // BASIC
  if (!form.employee_type) newErrors.employee_type = "Employee type is required";
  if (!form.first_name?.trim()) newErrors.first_name = "First name is required";
  if (!form.last_name?.trim()) newErrors.last_name = "Last name is required";

  // PHONES
  if (form.phone_personal.length !== 10)
    newErrors.phone_personal = "Personal phone must be 10 digits";

  if (form.phone_official.length !== 10)
    newErrors.phone_official = "Official phone must be 10 digits";

  if (
    form.phone_personal &&
    form.phone_official &&
    form.phone_personal === form.phone_official
  ) {
    newErrors.phone_official = "Personal and official phone cannot be same";
  }

  // EMAIL
  if (
    !form.official_email ||
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.official_email)
  ) {
    newErrors.official_email = "Valid official email required";
  }

  // CATEGORY
  if (!form.category.length)
    newErrors.category = "Select at least one category";

  // USER FLOW
  if (!form.user_type)
    newErrors.user_type = "User type is required";

  if (!form.position)
    newErrors.position = "Position is required";

  if (form.position === "EXECUTIVE" && !form.report_to)
    newErrors.report_to = "Reporting manager is required";

  if (!form.handle.length)
    newErrors.handle = "Select at least one handle";

  if (!form.location_mode)
    newErrors.location_mode = "Location mode is required";

  // LOCATION
  if (form.location_mode === "state" && !form.locations.length)
    newErrors.locations = "Select at least one state";

  if (form.location_mode === "district") {
    if (!form.selected_state)
      newErrors.selected_state = "State is required";

    if (!form.locations.length)
      newErrors.locations = "Select at least one district";
  }

  if (form.location_mode === "pincode" && !form.locations.length)
    newErrors.locations = "Add at least one pincode";

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors before submitting");
      return;
    }

    if (submitting) return;
    setSubmitting(true);

  try {
    const sessionUser = JSON.parse(localStorage.getItem("authUserData"));
    const companyId = sessionUser?.id;

    if (!companyId) {
      toast.error("Company not found");
      return;
    }

    // ---------------- LOCATION PAYLOAD ----------------
    let state = "";
    let district = "";
    let pincodes = [];

    if (form.location_mode === "state") {
      state = form.locations.join(",");
    }

    if (form.location_mode === "district") {
      state = form.selected_state;
      district = form.locations.join(",");
    }

    if (form.location_mode === "pincode") {
      pincodes = form.locations.map((l) => l.pincode);
    }

    const payload = {

      employee_type:form.employee_type,
      company_id: companyId,
      first_name: form.first_name,
      middle_name: form.middle_name,
      last_name: form.last_name,
      personal_phone: form.phone_personal,
      official_phone: form.phone_official,
      official_email: form.official_email,
      type_of_user: form.user_type,
      position: form.position,
      reports_to: form.position === "EXECUTIVE" ? form.report_to : null,
      categories: form.category,
      handle: form.handle,
      pincodes,
      state,
      district,
      location_mode: form.location_mode,
      // password: "123456",
      photo_url: photoUrl || "",
      logo: sessionUser?.logo || null,
      domain: sessionUser?.domain || null,
      title: sessionUser?.title || null,
    };

    if (isEdit) {
      await axios.post(
        `${BASE_URL}company/employee/update/${employee.id}`,
        payload
      );
      toast.success("Employee updated successfully");

      if (!isEdit) {
        // 🔥 Clear form
        setForm(initialFormState);
        setPhotoUrl("");

        // 🔥 Clear manager-related derived state
        setManagerStates([]);
        setManagerDistricts([]);
        setManagerLocationMode(null);
        setDistrictList([]);
      }

    } else {
      await axios.post(
        `${BASE_URL}company/employee/store`,
        payload
      );
      toast.success("Employee created successfully");

      setTimeout(() => {
        navigate("/allemployees");
      }, 1000);
      if (!isEdit) {
        // 🔥 Clear form
        setForm(initialFormState);
        setPhotoUrl("");

        // 🔥 Clear manager-related derived state
        setManagerStates([]);
        setManagerDistricts([]);
        setManagerLocationMode(null);
        setDistrictList([]);
      }
    }

    onSubmit?.();
    onClose?.();

  } catch (error) {
    console.error(error);
    toast.error(
      error?.response?.data?.message || "Failed to save employee"
    );
  } finally {
    setSubmitting(false); // 🔓 ALWAYS unlock
  }
};

// useEffect(() => {
//   if (managerStates.length === 1) {
//     setForm(p => ({ ...p, selected_state: managerStates[0] }));
//     fetchDistricts(managerStates[0]);
//   }
// }, [managerStates]);

// useEffect(() => {

//   if (isEdit) return; 
//   // 🔥 When position changes OR cleared
//   if (!form.position) {
//     // hard reset when cleared
//   }

//   // Clear manager-derived data
//   setManagerStates([]);
//   setManagerDistricts([]);
//   setManagerLocationMode(null);
//   setDistrictList([]);

//   // Reset location-related form fields
//   setForm(prev => ({
//     ...prev,
//     report_to: "",
//     location_mode: "state",
//     selected_state: isEdit ? prev.selected_state : "",
//     locations: isEdit ? prev.locations : [],
//     pincode: "",
//   }));
//   // setForm(prev => ({
//   //   ...prev,
//   //   report_to: "",
//   //   location_mode: "state",
//   //   selected_state: "",
//   //   locations: [],
//   //   pincode: "",
//   // }));

// }, [form.position, isEdit]);

useEffect(() => {
  if (isEdit) return;   // 🔥 NEVER touch edit mode

  // Only reset when switching position manually in CREATE mode
  if (!form.position) return;

  setManagerStates([]);
  setManagerDistricts([]);
  setManagerLocationMode(null);
  setDistrictList([]);

  setForm(prev => ({
    ...prev,
    report_to: "",
    location_mode: "state",
    selected_state: "",
    locations: [],
    pincode: "",
  }));

}, [form.position]);


useEffect(() => {
  if (!isEdit) return;
  if (!employee?.reports_to) return;
  if (managers.length === 0) return;

  handleManagerSelect(employee.reports_to);

  // 🔥 run only once
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [managers]);


useEffect(() => {
  if (Array.isArray(parentManagers) && parentManagers.length > 0) {
    setManagers(parentManagers);
  }
}, [parentManagers]);

  useEffect(() => {
  console.log("FINAL MANAGERS:", managers);
}, [managers]);

console.log("form.locations:", form.locations);
console.log("managerDistricts:", managerDistricts);


  return (
    <div className="mx-auto bg-white p-2 rounded-xl shadow-sm mt-4 mb-10">
      <h2 className="text-xl font-semibold text-[#0A3E91] mb-4">
        {isEdit ? "Update Employee" : "Add New Employee"}
      </h2>


      <form className="space-y-4" onSubmit={handleSubmit}>
        {/* ---------------- BASIC DETAILS ---------------- */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <SelectField
            label="Employee Type"
            required
            value={form.employee_type}
            error={errors.employee_type}
            onChange={(val) =>
              setForm((p) => ({ ...p, employee_type: val }))
            }
            options={[
              { label: "Field", value: "field" },
              { label: "Inhouse", value: "inhouse" },
              { label: "Inhouse TC", value: "inhouse_tc" },
            ]}
          />

          <InputField
            label="First Name"
            required
            value={form.first_name}
            error={errors.first_name}
            onChange={(val) =>
              setForm((p) => ({ ...p, first_name: val }))
            }
          />

          <InputField
            label="Middle Name"
            value={form.middle_name}
            onChange={(val) =>
              setForm((p) => ({ ...p, middle_name: val }))
            }
          />

          <InputField
            label="Last Name"
            required
            value={form.last_name}
            error={errors.last_name}
            onChange={(val) =>
              setForm((p) => ({ ...p, last_name: val }))
            }
          />

          <InputField
            label="Personal Phone"
            value={form.phone_personal}
            error={errors.phone_personal}
            inputMode="numeric"        // 📱 mobile keypad
            pattern="[0-9]*"
            maxLength={10}             // 🔒 max 10 digits
            onChange={(val) => {
              const digitsOnly = val.replace(/\D/g, ""); // remove non-numbers
              if (digitsOnly.length <= 10) {
                setForm((p) => ({ ...p, phone_personal: digitsOnly }));
              }
            }}
          />

          <InputField
            label="Official Phone"
            value={form.phone_official}
            error={errors.phone_official}
            inputMode="numeric"        // 📱 mobile keypad
            pattern="[0-9]*"
            maxLength={10}             // 🔒 max 10 digits
            onChange={(val) => {
              const digitsOnly = val.replace(/\D/g, ""); // allow numbers only
              if (digitsOnly.length <= 10) {
                setForm((p) => ({ ...p, phone_official: digitsOnly }));
              }
            }}
          />

          <InputField
            label="Official Email"
            type="email"
            value={form.official_email}
            error={errors.official_email}
            onChange={(val) =>
              setForm((p) => ({ ...p, official_email: val }))
            }
          />

          {/* <InputField
            label="Employee ID"
            value={form.employee_id}
            onChange={(val) =>
              setForm((p) => ({ ...p, employee_id: val }))
            }
          /> */}

          <InputField
            label="Employee Photo"
            type="file"
            helper={uploadLoading ? "Uploading..." : ""}
            onChange={handleImageUpload}
          />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <MultiSelectChips
            label="Category"
            required
            loading={loadingCategories}
            value={form.category}
            error={errors.category}
            onToggle={(val) => toggleMultiSelect("category", val)}
            items={categories.map((cat) => ({
              label: cat.name,
              value: cat.name,
            }))}
          />

        <CategoryGuard blocked={!isCategorySelected}>
          <SelectField
              label="User Type"
              value={form.user_type}
              error={errors.user_type}
              // disabled={!isCategorySelected}
              onChange={(val) =>
                setForm((p) => ({ ...p, user_type: val }))
              }
              options={[
                { label: "Sales", value: "Sales" },
                { label: "Operation", value: "Operation" },
              ]}
            />
        </CategoryGuard>

        <CategoryGuard blocked={!isCategorySelected}>
          <SelectField
            label="Position"
            required
            value={form.position}
            error={errors.position}
            // disabled={!isCategorySelected}
            onChange={(val) => updateField("position", val)}
            options={[
              { label: "Manager", value: "MANAGER" },
              { label: "Executive", value: "EXECUTIVE" },
            ]}
          />
        </CategoryGuard>

        {form.position === "EXECUTIVE" && (
          <CategoryGuard blocked={!isCategorySelected}>
            <SelectField
              label="Report To"
              required
              value={form.report_to}
              onChange={handleManagerSelect}
              options={managers.map(m => ({
                label: `${m.first_name} ${m.last_name}`,
                value: m.id,
              }))}
            />
          </CategoryGuard>
        )}
          <CategoryGuard blocked={!isCategorySelected}>
            <MultiSelectChips
              label="Handle"
              required
              value={form.handle}
              // onToggle={(val) => {
              //   if (!isCategorySelected) return;
              //   if (form.position === "EXECUTIVE") return; // existing lock
              //   toggleMultiSelect("handle", val);
              // }}

              onToggle={(val) => {
                if (!isCategorySelected) return;
                toggleMultiSelect("handle", val);
              }}
              items={HANDLES.map((h) => ({
                label: h,
                value: h,
              }))}
            />
          </CategoryGuard>
        

        


        {/* <CategoryGuard blocked={!isCategorySelected}>
          {form.position === "EXECUTIVE" && (
            <SelectField
              label="Report To"
              required
              value={form.report_to}
              error={errors.report_to}
              disabled={!isCategorySelected || loadingManagers}
              onChange={handleManagerSelect}
              helper={
                loadingManagers
                  ? "Loading managers..."
                  : managers.length === 0
                  ? "No managers available"
                  : ""
              }
              options={
                loadingManagers
                  ? []
                  : managers.map(m => ({
                      label: `${m.first_name} ${m.last_name}`,
                      value: m.id,
                    }))
              }
            />
          )}
        </CategoryGuard> */}

        {/* {form.position === "MANAGER" && (
          <CategoryGuard blocked={!isCategorySelected}>
            <MultiSelectChips
              label="Handle"
              required
              value={form.handle}
              onToggle={(val) => {
                if (!isCategorySelected) return;
                if (form.position === "EXECUTIVE") return; // existing lock
                toggleMultiSelect("handle", val);
              }}
              items={HANDLES.map((h) => ({
                label: h,
                value: h,
              }))}
            />
          </CategoryGuard>
        )} */}

        {/* {form.position === "EXECUTIVE" && (
          <CategoryGuard blocked={!isCategorySelected}>
            <SelectField
              label="Report To"
              required
              value={form.report_to}
              onChange={handleManagerSelect}
              options={managers.map(m => ({
                label: `${m.first_name} ${m.last_name}`,
                value: m.id,
              }))}
            />
          </CategoryGuard>
        )} */}


        {managerStates.length === 0 && (
          <CategoryGuard disabled={!isCategorySelected}>
          <SelectField
            label="Location Mode"
            value={form.location_mode}
            error={errors.location_mode}
            disabled={!isCategorySelected}   // 🔒 DISABLED
            onChange={(val) =>
              setForm(prev => ({
                ...prev,
                location_mode: val,
                selected_state: "",
                locations: [],
                pincode: "",
              }))
            }
            options={[
              { label: "State Only", value: "state" },
              { label: "State → District", value: "district" },
              { label: "Pincode", value: "pincode" },
            ]}
          />
          </CategoryGuard>
        )}

        {managerStates.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Manager Assigned States
            </label>

            <SelectedTags
              items={managerStates}
              readOnly
            />
          </div>
        )}

        {isManager && (form.location_mode === "state" && managerStates.length === 0) && (
          <>
            <SelectField
              label="Select State"
              onChange={(val) => {
                if (!form.locations.includes(val)) {
                  setForm(p => ({
                    ...p,
                    locations: [...p.locations, val],
                  }));
                }
              }}
              options={stateList.map(s => ({
                label: s.state,
                value: s.state,
              }))}
            />

            <SelectedTags
              items={form.locations}
              onRemove={(state) =>
                setForm(p => ({
                  ...p,
                  locations: p.locations.filter(s => s !== state),
                }))
              }
            />
          </>
        )}  

        {form.location_mode === "district" && (
          <>
            <div>
              <SelectField
              label="Select State"
              value={form.selected_state}
              onChange={(val) => {
                setForm(p => ({
                  ...p,
                  selected_state: val,
                  locations: isEdit && val === p.selected_state
                    ? p.locations      // ✅ keep existing districts
                    : [],              // reset only on real change
                }));
                fetchDistricts(val);
              }}
              options={
                form.position === "MANAGER"
                  ? stateList.map(s => ({
                      label: s.state,
                      value: s.state,
                    }))
                  : stateList
                      .filter(s => managerStates.includes(s.state.toLowerCase()))
                      .map(s => ({
                        label: s.state,
                        value: s.state,
                      }))
                    }

            />



            {managerStates.length > 0 && (
              <p className="text-xs text-slate-500 mt-1">
                Districts limited to manager’s assigned states
              </p>
            )}
            </div>

            <div>
              <SearchableMultiSelect
              label="Select District(s)"
              items={visibleDistricts}
              selected={form.locations}
              disabled={loadingDistricts}

              placeholder={
                loadingDistricts
                  ? `Searching districts for ${form.selected_state}...`
                  : "Search district"
              }

              onSelect={(district) => {
                // ❌ block adding outside manager districts
                if (
                  managerLocationMode === "district" &&
                  !managerDistricts.includes(district)
                ) return;

                if (!form.locations.includes(district)) {
                  setForm(p => ({
                    ...p,
                    locations: [...p.locations, district],
                  }));
                }
              }}

              onRemove={(district) =>
                setForm(p => ({
                  ...p,
                  locations: p.locations.filter(d => d !== district),
                }))
              }
            />


            {managerLocationMode === "district" && (
              <p className="text-xs text-slate-500 mt-1">
                Districts inherited from manager. You can add/remove only these districts.
              </p>
            )}

            {managerLocationMode === "state" && (
              <p className="text-xs text-slate-500 mt-1">
                Select any district from the chosen state.
              </p>
            )}
            </div>


          </>
        )}

        {/* ---------------- STATE MODE ---------------- */}
        {form.location_mode === "pincode" && (
          <>
            <InputField
              label="Pincode"
              value={form.pincode}
              placeholder="Enter 6 digit pincode"
              onChange={(val) => {
                if (val.length === 6) fetchFromPincode(val);
                setForm((p) => ({ ...p, pincode: val }));
              }}
            />


                  {form.locations.length > 0 && (
              <div className="mt-2 space-y-2">
                {form.locations.map((l) => (
                  <div
                    key={l.pincode}
                    className="flex justify-between items-center bg-blue-50 border p-2 rounded text-sm"
                  >
                    <span>
                      {l.pincode} – {l.district}, {l.state}
                    </span>
                    <button
                      onClick={() =>
                        setForm((p) => ({
                          ...p,
                          locations: p.locations.filter(
                            (x) => x.pincode !== l.pincode
                          ),
                        }))
                      }
                      className="text-red-600 font-bold"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        </div>

        {/* ---------------- PINCODE MODE ---------------- */}
        {/* {form.location_mode === "pincode" && (
          <>
            <InputField
              label="Pincode"
              value=""
              placeholder="Enter 6 digit pincode"
              onChange={(val) => {
                if (val.length === 6) fetchStateDistrictFromPin(val);
              }}
            />

            {loadingPin && (
              <p className="text-xs text-blue-500">Checking pincode...</p>
            )}

            {form.pinned_state && (
              <div className="text-sm bg-blue-50 border p-2 rounded">
                <p><strong>State:</strong> {form.pinned_state}</p>
                <p><strong>District:</strong> {form.pinned_district}</p>
              </div>
            )}
          </>
        )} */}

        {/* ---------------- ACTIONS ---------------- */}
        <div className="flex justify-end gap-2 pt-4">
          <BlueButton type="button" onClick={() => navigate("/allemployees")}>
            Cancel
          </BlueButton>
         <BlueButton
            type="submit"
            disabled={submitting || uploadLoading}
          >
            {submitting
              ? isEdit
                ? "Updating..."
                : "Saving..."
              : isEdit
              ? "Update Employee"
              : "Save Employee"}
          </BlueButton>
        </div>
      </form>
    </div>
  );
}


function SelectedTags({ items = [], onRemove }) {
  if (!Array.isArray(items) || items.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 m-3 p-2">
      {items.map((item) => (
        <span
          key={item}
          className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs flex gap-2 items-center"
        >
          {item}
          <button
            type="button"
            onClick={() => onRemove?.(item)} // ✅ SAFE CALL
            className="text-red-600 font-bold"
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
}


// import { useEffect, useState } from "react";
// import axios from "axios";
// import Swal from "sweetalert2";
// import { useNavigate } from "react-router-dom";

// import BlueButton from "../../../Component/Common/BlueButton";
// import SelectField from "../../../Component/RetailerComponents/SelectField";
// import InputField from "../../../Component/RetailerComponents/InputField";
// import MultiSelectChips from "../../../Component/RetailerComponents/MultiSelectChips";
// import { BASE_URL } from "../../../config/api";
// import toast from "react-hot-toast";
// import SearchableMultiSelect from "./SearchableMultiSelect";


// function CategoryGuard({
//   blocked,
//   tooltip = "Please select Category first",
//   children,
// }) {
//   return (
//     <div className="relative group">
//       {/* TOOLTIP */}
//       {blocked && (
//         <div className="
//           pointer-events-none
//           absolute -top-9 left-1/2 -translate-x-1/2
//           whitespace-nowrap
//           rounded-md bg-slate-900 px-3 py-1
//           text-xs text-white
//           opacity-0 group-hover:opacity-100
//           transition
//           z-20
//         ">
//           {tooltip}
//           <span className="
//             absolute left-1/2 top-full -translate-x-1/2
//             border-4 border-transparent border-t-slate-900
//           " />
//         </div>
//       )}

//       {/* CLICK / HOVER BLOCKER */}
//       {blocked && (
//         <div className="absolute inset-0 z-10 cursor-not-allowed" />
//       )}

//       {/* FIELD */}
//       <div className={blocked ? "opacity-60" : ""}>
//         {children}
//       </div>
//     </div>
//   );
// }


// export default function EmployeeForm({ managers: parentManagers, employee, onSubmit, onClose }) {

//   const [managers, setManagers] = useState(parentManagers || []);
//   const navigate = useNavigate();
  
//   const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);
//   const [selectedDistrictText, setSelectedDistrictText] = useState("");
//   const [loadingPin, setLoadingPin] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const [loadingCategories, setLoadingCategories] = useState(false);
//   const [uploadLoading, setUploadLoading] = useState(false);
//   const [photoUrl, setPhotoUrl] = useState("");
//   // const [managers, setManagers] = useState([]);
//   const [loadingManagers, setLoadingManagers] = useState(false);
//   const [managerStates, setManagerStates] = useState([]);
//   const [isManagerDistrictLocked, setIsManagerDistrictLocked] = useState(false);
//   const [managerDistricts, setManagerDistricts] = useState([]);
//   const [managerLocationMode, setManagerLocationMode] = useState(null);
//   const [loadingDistricts, setLoadingDistricts] = useState(false);
//   const [submitting, setSubmitting] = useState(false);
//   const isEdit = Boolean(employee?.id);
//   const initialFormState = {
//     employee_type: "",
//     position: "",
//     first_name: "",
//     middle_name: "",
//     last_name: "",
//     phone_personal: "",
//     phone_official: "",
//     official_email: "",
//     employee_id: "",
//     photo: null,

//     category: [],
//     user_type: "",
//     handle: [],

//     report_to: "",
//     location_mode: "state",
//     selected_state: "",
//     locations: [],
//     pinned_state: "",
//     pinned_district: "",
//     // password: 123456,
//   };

//   const [form, setForm] = useState(initialFormState);
//   const isCategorySelected = form.category.length > 0;
//   const isManager = form.position === "MANAGER";

//   const sessionUser = JSON.parse(localStorage.getItem("authUserData"));
//   const companyId = sessionUser?.id;

// useEffect(() => {
//   if (!employee) return;

//   const position =
//     employee.position?.toUpperCase() === "MANAGER"
//       ? "MANAGER"
//       : "EXECUTIVE";

//   const locationMode = employee.location_mode || "state";

//   setForm(prev => ({
//     ...prev,

//     employee_type: employee.employee_type
//       ? employee.employee_type.toLowerCase()
//       : "",
//     position: employee.position
//       ? employee.position.toUpperCase()
//       : "",

//     first_name: employee.first_name || "",
//     middle_name: employee.middle_name || "",
//     last_name: employee.last_name || "",

//     phone_personal: employee.personal_phone || "",
//     phone_official: employee.official_phone || "",
//     official_email: employee.official_email || "",

//     user_type: employee.type_of_user || "",
//     report_to: Number(employee.reports_to) || "",

//     category: employee.categories || [],
//     handle: employee.handle || [],

//     location_mode: locationMode,

//     selected_state:
//       locationMode === "district" || locationMode === "state"
//         ? employee.state || ""
//         : "",

//     locations:
//       locationMode === "district"
//         ? employee.district?.split(",").map(d => d.trim()) || []
//         : locationMode === "state"
//         ? employee.state?.split(",").map(s => s.trim()) || []
//         : employee.pincodes?.map(p => ({
//             pincode: p,
//             state: employee.state,
//             district: employee.district,
//           })) || [],
//   }));

//   setPhotoUrl(employee.photo_url || "");
// }, [employee]);


//   useEffect(() => {
//     if (!isEdit) return;
//     if (!employee?.reports_to) return;
//     if (managers.length === 0) return;

//     setForm(prev => ({
//       ...prev,
//       report_to: Number(employee.reports_to),
//     }));
//   }, [managers, isEdit, employee]);


//   const handleImageUpload = async (e) => {
//   const file = e.target.files[0];
//   if (!file) return;

//   setUploadLoading(true);

//   const formData = new FormData();
//   formData.append("file", file);
//   formData.append("tag", "Employee");

//   try {
//     const res = await fetch(`${BASE_URL}warranty/upload-file`, {
//       method: "POST",
//       body: formData,
//     });

//     const data = await res.json();

//     if (data?.data?.file_url) {
//       setPhotoUrl(data.data.file_url);
//       toast.success("Image uploaded");
//     }
//   } catch {
//     toast.error("Image upload failed");
//   } finally {
//     setUploadLoading(false);
//   }
// };

//   const [errors, setErrors] = useState({});
//   const [districtList, setDistrictList] = useState([]);

//   const updateField = (field, value) => {
//     setForm((prev) => ({
//       ...prev,
//       [field]: value,
//       ...(field === "position" && value === "MANAGER"
//         ? { report_to: "" }
//         : {}),
//     }));
//   };

//    function toggleMultiSelect(name, value) {
//     setForm(prev => {
//       const list = new Set(prev[name]);
//       list.has(value) ? list.delete(value) : list.add(value);
//       return { ...prev, [name]: [...list] };
//     });
//   }

//   /* ---------------- STATES ---------------- */
//   const stateList = [
//     { code: "AP", state: "ANDHRA PRADESH" },
//     { code: "TG", state: "TELANGANA" },
//     { code: "AS", state: "ASSAM" },
//     { code: "BR", state: "BIHAR" },
//     { code: "CT", state: "CHHATTISGARH" },
//     { code: "DL", state: "DELHI" },
//     { code: "GA", state: "GOA" },
//     { code: "GJ", state: "GUJARAT" },
//     { code: "HR", state: "HARYANA" },
//     { code: "HP", state: "HIMACHAL PRADESH" },
//     { code: "JK", state: "JAMMU AND KASHMIR" },
//     { code: "JH", state: "JHARKHAND" },
//     { code: "KA", state: "KARNATAKA" },
//     { code: "KL", state: "KERALA" },
//     { code: "MP", state: "MADHYA PRADESH" },
//     { code: "MH", state: "MAHARASHTRA" },
//     { code: "OD", state: "ODISHA" },
//     { code: "PB", state: "PUNJAB" },
//     { code: "RJ", state: "RAJASTHAN" },
//     { code: "TN", state: "TAMIL NADU" },
//     { code: "UP", state: "UTTAR PRADESH" },
//     { code: "UK", state: "UTTARAKHAND" },
//     { code: "WB", state: "WEST BENGAL" },
//   ];

//   const HANDLES = ["AGENT", "RETAILER", "END_USER"];

//   useEffect(() => {
//     async function fetchCategories() {
//       try {
//         setLoadingCategories(true);
//         const res = await fetch(`${BASE_URL}warranty/get-categories`);
//         const data = await res.json();
//         if (data) {
//           const active = data.filter(cat => cat.status === 1);
//           setCategories(active);
//         }
//       } catch (error) {
//         console.error("Category fetch error", error);
//         toast.error("Failed to load categories");
//       } finally {
//         setLoadingCategories(false);
//       }
//     }
//     fetchCategories();
//   }, []);

//   useEffect(() => {
//   if (!isCategorySelected) return;
//   if (!form.user_type) return;

//   async function fetchManagers() {
//     try {
//       setLoadingManagers(true);

//       const res = await axios.post(
//         `${BASE_URL}company/employee/all`,
//         {
//           company_id: companyId,
//           type_of_user: form.user_type, // 🔥 Sales / Operation
//           position: "MANAGER",
//         }
//       );

//       if (res.data?.status) {
//         setManagers(res.data.data?.data || []);
//       }
//     } catch (err) {
//       toast.error("Failed to load managers");
//     } finally {
//       setLoadingManagers(false);
//     }
//   }

//   fetchManagers();
// }, [form.user_type, isCategorySelected]);


// useEffect(() => {
//   if (isEdit) return; // 🔥 THIS LINE FIXES IT
//   if (isCategorySelected) return;

//   setForm(prev => ({
//     ...prev,
//     user_type: "",
//     position: "",
//     report_to: "",
//     handle: [],
//   }));

//   setManagers([]);
// }, [isCategorySelected, isEdit]);


// //   useEffect(() => {
// //   if (form.position !== "EXECUTIVE") return;

// //   async function fetchManagers() {
// //     try {
// //       setLoadingManagers(true);

// //       const sessionUser = JSON.parse(localStorage.getItem("authUserData"));
// //       const companyId = sessionUser?.id;

// //       const res = await axios.post(
// //         `${BASE_URL}company/employee/all?position=MANAGER`,
// //         { company_id: companyId }
// //       );

// //       if (res.data?.status) {
// //         // ✅ ALWAYS ARRAY
// //         setManagers(res.data.data?.data || []);
// //       }
// //     } catch (err) {
// //       toast.error("Failed to load managers");
// //     } finally {
// //       setLoadingManagers(false);
// //     }
// //   }

// //   fetchManagers();
// // }, [form.position]);

//   const resetManagerDerivedData = () => {
//     setManagerStates([]);
//     setDistrictList([]);

//     setForm(prev => ({
//       ...prev,
//       location_mode: "state",
//       selected_state: "",
//       locations: [],
//     }));
//   };

//   const handleManagerSelect = (managerId) => {
//   const manager = managers.find(m => m.id === Number(managerId));
//   if (!manager) return;

//   // 🔥 AUTO HANDLE INHERIT
//   const managerHandles = Array.isArray(manager.handle)
//     ? manager.handle
//     : [];

//   // reset everything first
//   setManagerStates([]);
//   setManagerDistricts([]);
//   setDistrictList([]);

//   setManagerLocationMode(manager.location_mode);

//   setForm(prev => ({
//     ...prev,
//     report_to: manager.id,
//     handle: managerHandles, // ✅ AUTO SET HANDLE
//   }));

//   // ---------- STATE MODE ----------
//   if (manager.location_mode === "state") {
//     const states = manager.state
//       ? manager.state.split(",").map(s => s.trim().toLowerCase())
//       : [];

//     setManagerStates(states);

//     setForm(prev => ({
//       ...prev,
//       location_mode: "district",
//       selected_state: "",
//       locations: [],
//     }));

//     return;
//   }

//   // ---------- DISTRICT MODE ----------
//   if (manager.location_mode === "district") {
//     const districts = manager.district
//       ? manager.district.split(",").map(d => d.trim())
//       : [];

//     setManagerStates([manager.state.toLowerCase()]);
//     setManagerDistricts(districts);

//     setForm(prev => ({
//       ...prev,
//       location_mode: "district",
//       selected_state: manager.state,
//       locations: districts,
//     }));

//     fetchDistricts(manager.state);
//   }
// };

//   /* ---------------- FETCH DISTRICTS ---------------- */
//   const fetchDistricts = async (stateName) => {
//     try {
//       setLoadingDistricts(true);      // 🔥 start loader
//       setDistrictList([]);

//       const res = await axios.get(
//         `https://goelectronix.in/api/v1/org_user/statedistrict?state=${stateName}`
//       );

//       setDistrictList(res.data || []);
//     } catch {
//       Swal.fire("Error", "Failed to fetch districts", "error");
//     } finally {
//       setLoadingDistricts(false);     // 🔥 stop loader
//     }
//   };

//   const visibleDistricts =
//   managerLocationMode === "district"
//     ? districtList
//         .map(d => d.district)
//         .filter(d => managerDistricts.includes(d))
//     : districtList.map(d => d.district);


//   /* ---------------- PINCODE LOOKUP ---------------- */
//  async function fetchFromPincode(pin) {
//   const res = await axios.get(
//     `https://goelectronix.in/public/api/v1/pincode/${pin}`
//   );

//   if (!res.data?.success) return;

//   setForm((prev) => {
//     const exists = prev.locations.some(
//       (l) => l.pincode === pin
//     );

//     if (exists) {
//       toast.error("Pincode already selected");
//       return prev;
//     }

//     return {
//       ...prev,
//       locations: [
//         ...prev.locations,
//         {
//           pincode: pin,
//           state: res.data.state,
//           district: res.data.district,
//         },
//       ],
//       pincode: "",
//     };
//   });
// }

//   const validateForm = () => {
//   const newErrors = {};

//   // BASIC
//   if (!form.employee_type) newErrors.employee_type = "Employee type is required";
//   if (!form.first_name?.trim()) newErrors.first_name = "First name is required";
//   if (!form.last_name?.trim()) newErrors.last_name = "Last name is required";

//   // PHONES
//   if (form.phone_personal.length !== 10)
//     newErrors.phone_personal = "Personal phone must be 10 digits";

//   if (form.phone_official.length !== 10)
//     newErrors.phone_official = "Official phone must be 10 digits";

//   if (
//     form.phone_personal &&
//     form.phone_official &&
//     form.phone_personal === form.phone_official
//   ) {
//     newErrors.phone_official = "Personal and official phone cannot be same";
//   }

//   // EMAIL
//   if (
//     !form.official_email ||
//     !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.official_email)
//   ) {
//     newErrors.official_email = "Valid official email required";
//   }

//   // CATEGORY
//   if (!form.category.length)
//     newErrors.category = "Select at least one category";

//   // USER FLOW
//   if (!form.user_type)
//     newErrors.user_type = "User type is required";

//   if (!form.position)
//     newErrors.position = "Position is required";

//   if (form.position === "EXECUTIVE" && !form.report_to)
//     newErrors.report_to = "Reporting manager is required";

//   if (!form.handle.length)
//     newErrors.handle = "Select at least one handle";

//   if (!form.location_mode)
//     newErrors.location_mode = "Location mode is required";

//   // LOCATION
//   if (form.location_mode === "state" && !form.locations.length)
//     newErrors.locations = "Select at least one state";

//   if (form.location_mode === "district") {
//     if (!form.selected_state)
//       newErrors.selected_state = "State is required";

//     if (!form.locations.length)
//       newErrors.locations = "Select at least one district";
//   }

//   if (form.location_mode === "pincode" && !form.locations.length)
//     newErrors.locations = "Add at least one pincode";

//   setErrors(newErrors);
//   return Object.keys(newErrors).length === 0;
// };

//   /* ---------------- SUBMIT ---------------- */
//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!validateForm()) {
//       toast.error("Please fix the errors before submitting");
//       return;
//     }

//     if (submitting) return;
//     setSubmitting(true);

//   try {
//     const sessionUser = JSON.parse(localStorage.getItem("authUserData"));
//     const companyId = sessionUser?.id;

//     if (!companyId) {
//       toast.error("Company not found");
//       return;
//     }

//     // ---------------- LOCATION PAYLOAD ----------------
//     let state = "";
//     let district = "";
//     let pincodes = [];

//     if (form.location_mode === "state") {
//       state = form.locations.join(",");
//     }

//     if (form.location_mode === "district") {
//       state = form.selected_state;
//       district = form.locations.join(",");
//     }

//     if (form.location_mode === "pincode") {
//       pincodes = form.locations.map((l) => l.pincode);
//     }

//     const payload = {

      
//       company_id: companyId,
//       first_name: form.first_name,
//       middle_name: form.middle_name,
//       last_name: form.last_name,
//       personal_phone: form.phone_personal,
//       official_phone: form.phone_official,
//       official_email: form.official_email,
//       type_of_user: form.user_type,
//       position: form.position,
//       reports_to: form.position === "EXECUTIVE" ? form.report_to : null,
//       categories: form.category,
//       handle: form.handle,
//       pincodes,
//       state,
//       district,
//       location_mode: form.location_mode,
//       password: "123456",
//       photo_url: photoUrl || "",
//     };

//     if (isEdit) {
//       await axios.post(
//         `${BASE_URL}company/employee/update/${employee.id}`,
//         payload
//       );
//       toast.success("Employee updated successfully");

//       if (!isEdit) {
//         // 🔥 Clear form
//         setForm(initialFormState);
//         setPhotoUrl("");

//         // 🔥 Clear manager-related derived state
//         setManagerStates([]);
//         setManagerDistricts([]);
//         setManagerLocationMode(null);
//         setDistrictList([]);
//       }

//     } else {
//       await axios.post(
//         `${BASE_URL}company/employee/store`,
//         payload
//       );
//       toast.success("Employee created successfully");
//       if (!isEdit) {
//         // 🔥 Clear form
//         setForm(initialFormState);
//         setPhotoUrl("");

//         // 🔥 Clear manager-related derived state
//         setManagerStates([]);
//         setManagerDistricts([]);
//         setManagerLocationMode(null);
//         setDistrictList([]);
//       }
//     }

//     onSubmit?.();
//     onClose?.();

//   } catch (error) {
//     console.error(error);
//     toast.error(
//       error?.response?.data?.message || "Failed to save employee"
//     );
//   } finally {
//     setSubmitting(false); // 🔓 ALWAYS unlock
//   }
// };

// useEffect(() => {
//   if (managerStates.length === 1) {
//     setForm(p => ({ ...p, selected_state: managerStates[0] }));
//     fetchDistricts(managerStates[0]);
//   }
// }, [managerStates]);

// useEffect(() => {

//   if (isEdit) return; 
//   // 🔥 When position changes OR cleared
//   if (!form.position) {
//     // hard reset when cleared
//   }

//   // Clear manager-derived data
//   setManagerStates([]);
//   setManagerDistricts([]);
//   setManagerLocationMode(null);
//   setDistrictList([]);

//   // Reset location-related form fields
//   setForm(prev => ({
//     ...prev,
//     report_to: "",
//     location_mode: "state",
//     selected_state: "",
//     locations: [],
//     pincode: "",
//   }));

// }, [form.position, isEdit]);

// useEffect(() => {
//   if (!isEdit || !employee || form.position !== "EXECUTIVE") return;
//   if (!employee.reports_to) return;

//   handleManagerSelect(employee.reports_to);
// }, [isEdit, employee, form.position]);

// useEffect(() => {
//   if (Array.isArray(parentManagers) && parentManagers.length > 0) {
//     setManagers(parentManagers);
//   }
// }, [parentManagers]);

//   useEffect(() => {
//   console.log("FINAL MANAGERS:", managers);
// }, [managers]);


//   return (
//     <div className="mx-auto bg-white p-6 rounded-xl shadow-sm mt-4 mb-10">
//       <h2 className="text-xl font-semibold text-[#0A3E91] mb-4">
//         {isEdit ? "Update Employee" : "Add New Employee"}
//       </h2>


//       <form className="space-y-4" onSubmit={handleSubmit}>
//         {/* ---------------- BASIC DETAILS ---------------- */}
//         <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
//           <SelectField
//             label="Employee Type"
//             required
//             value={form.employee_type}
//             error={errors.employee_type}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, employee_type: val }))
//             }
//             options={[
//               { label: "Field", value: "field" },
//               { label: "Inhouse", value: "inhouse" },
//               { label: "Inhouse TC", value: "inhouse_tc" },
//             ]}
//           />

//           <InputField
//             label="First Name"
//             required
//             value={form.first_name}
//             error={errors.first_name}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, first_name: val }))
//             }
//           />

//           <InputField
//             label="Middle Name"
//             value={form.middle_name}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, middle_name: val }))
//             }
//           />

//           <InputField
//             label="Last Name"
//             required
//             value={form.last_name}
//             error={errors.last_name}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, last_name: val }))
//             }
//           />

//           <InputField
//             label="Personal Phone"
//             value={form.phone_personal}
//             error={errors.phone_personal}
//             inputMode="numeric"        // 📱 mobile keypad
//             pattern="[0-9]*"
//             maxLength={10}             // 🔒 max 10 digits
//             onChange={(val) => {
//               const digitsOnly = val.replace(/\D/g, ""); // remove non-numbers
//               if (digitsOnly.length <= 10) {
//                 setForm((p) => ({ ...p, phone_personal: digitsOnly }));
//               }
//             }}
//           />

//           <InputField
//             label="Official Phone"
//             value={form.phone_official}
//             error={errors.phone_official}
//             inputMode="numeric"        // 📱 mobile keypad
//             pattern="[0-9]*"
//             maxLength={10}             // 🔒 max 10 digits
//             onChange={(val) => {
//               const digitsOnly = val.replace(/\D/g, ""); // allow numbers only
//               if (digitsOnly.length <= 10) {
//                 setForm((p) => ({ ...p, phone_official: digitsOnly }));
//               }
//             }}
//           />

//           <InputField
//             label="Official Email"
//             type="email"
//             value={form.official_email}
//             error={errors.official_email}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, official_email: val }))
//             }
//           />

//           {/* <InputField
//             label="Employee ID"
//             value={form.employee_id}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, employee_id: val }))
//             }
//           /> */}

//           <InputField
//             label="Employee Photo"
//             type="file"
//             helper={uploadLoading ? "Uploading..." : ""}
//             onChange={handleImageUpload}
//           />
//           </div>


//          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
//             {/* Category */}
//             <MultiSelectChips
//               label="Category"
//               required
//               value={form.category}
//               onToggle={(val) => toggleMultiSelect("category", val)}
//               items={categories.map(c => ({ label: c.name, value: c.name }))}
//             />

//             {/* User Type */}
//             <CategoryGuard blocked={!isCategorySelected}>
//               <SelectField
//                 label="User Type"
//                 value={form.user_type}
//                 onChange={(val) => setForm(p => ({ ...p, user_type: val }))}
//                 options={[
//                   { label: "Sales", value: "Sales" },
//                   { label: "Operation", value: "Operation" },
//                 ]}
//               />
//             </CategoryGuard>

//             {/* Position */}
//             <CategoryGuard blocked={!isCategorySelected}>
//               <SelectField
//                 label="Position"
//                 value={form.position}
//                 onChange={(val) => updateField("position", val)}
//                 options={[
//                   { label: "Manager", value: "MANAGER" },
//                   { label: "Executive", value: "EXECUTIVE" },
//                 ]}
//               />
//             </CategoryGuard>

//             {/* 4th column depends on role */}
//             {form.position === "MANAGER" && (
//               <CategoryGuard blocked={!isCategorySelected}>
//                 <MultiSelectChips
//                   label="Handle"
//                   required
//                   value={form.handle}
//                   onToggle={(val) => {
//                     if (!isCategorySelected) return;
//                     if (form.position === "EXECUTIVE") return; // existing lock
//                     toggleMultiSelect("handle", val);
//                   }}
//                   items={HANDLES.map((h) => ({
//                     label: h,
//                     value: h,
//                   }))}
//                 />
//               </CategoryGuard>
//             )}

//             {form.position === "EXECUTIVE" && (
//               <CategoryGuard blocked={!isCategorySelected}>
//                 <SelectField
//                   label="Report To"
//                   required
//                   value={form.report_to}
//                   onChange={handleManagerSelect}
//                   options={managers.map(m => ({
//                     label: `${m.first_name} ${m.last_name}`,
//                     value: m.id,
//                   }))}
//                 />
//               </CategoryGuard>
//             )}
//           </div>

//           {form.position === "MANAGER" && (
//             <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
//               {/* Location Mode */}
//               <CategoryGuard blocked={!isCategorySelected}>
//                 <SelectField
//                   label="Location Mode"
//                   value={form.location_mode}
//                   onChange={(val) =>
//                     setForm(p => ({
//                       ...p,
//                       location_mode: val,
//                       selected_state: "",
//                       locations: [],
//                     }))
//                   }
//                   options={[
//                     { label: "State Only", value: "state" },
//                     { label: "State → District", value: "district" },
//                     { label: "Pincode", value: "pincode" },
//                   ]}
//                 />
//               </CategoryGuard>

//               {/* Select State */}
//               {/* <SelectField
//                 label="Select State"
//                 onChange={(val) =>
//                   setForm(p => ({
//                     ...p,
//                     locations: [...new Set([...p.locations, val])],
//                   }))
//                 }
//                 options={stateList.map(s => ({
//                   label: s.state,
//                   value: s.state,
//                 }))}
//               /> */}

//               {form.position === "MANAGER" && form.location_mode === "state" && (
//                 // <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
//                 <>
//                   <SelectField
//                     label="Select State"
//                     onChange={(val) => {
//                       if (!form.locations.includes(val)) {
//                         setForm(p => ({
//                           ...p,
//                           locations: [...p.locations, val],
//                         }));
//                       }
//                     }}
//                     options={stateList.map(s => ({
//                       label: s.state,
//                       value: s.state,
//                     }))}
//                   />

//                   <SelectedTags
//                     items={form.locations}
//                     onRemove={(state) =>
//                       setForm(p => ({
//                         ...p,
//                         locations: p.locations.filter(s => s !== state),
//                       }))
//                     }
//                   />
//                  {/* </div> */}
//                  </>
//               )}

//               {/* {form.location_mode === "district" && (
//           <>

//           <SelectField
//               label="Select State"
//               value={form.selected_state}
//               onChange={(val) => {
//                 setForm(p => ({
//                   ...p,
//                   selected_state: val,
//                   locations: [],
//                 }));
//                 fetchDistricts(val);
//               }}
//               options={
//                 form.position === "MANAGER"
//                   ? stateList.map(s => ({
//                       label: s.state,
//                       value: s.state,
//                     }))
//                   : stateList
//                       .filter(s => managerStates.includes(s.state.toLowerCase()))
//                       .map(s => ({
//                         label: s.state,
//                         value: s.state,
//                       }))
//                     }

//             />
          
//             <SearchableMultiSelect
//               label="Select District(s)"
//               items={visibleDistricts}
//               selected={form.locations}
//               disabled={loadingDistricts}

//               placeholder={
//                 loadingDistricts
//                   ? `Searching districts for ${form.selected_state}...`
//                   : "Search district"
//               }

//               onSelect={(district) => {
//                 // ❌ block adding outside manager districts
//                 if (
//                   managerLocationMode === "district" &&
//                   !managerDistricts.includes(district)
//                 ) return;

//                 if (!form.locations.includes(district)) {
//                   setForm(p => ({
//                     ...p,
//                     locations: [...p.locations, district],
//                   }));
//                 }
//               }}

//               onRemove={(district) =>
//                 setForm(p => ({
//                   ...p,
//                   locations: p.locations.filter(d => d !== district),
//                 }))
//               }
//             />


//             {managerLocationMode === "district" && (
//               <p className="text-xs text-slate-500 mt-1">
//                 Districts inherited from manager. You can add/remove only these districts.
//               </p>
//             )}

//             {managerLocationMode === "state" && (
//               <p className="text-xs text-slate-500 mt-1">
//                 Select any district from the chosen state.
//               </p>
//             )}


//           </>
//         )} */}

//         {form.location_mode === "district" && (
//       <>
//              <SelectField
//                label="Select State"
//                value={form.selected_state}
//                onChange={(val) => {
//                  setForm(p => ({
//                    ...p,
//                    selected_state: val,
//                    locations: [],
//                  }));
//                  fetchDistricts(val);
//                }}
//                options={
//                  form.position === "MANAGER"
//                    ? stateList.map(s => ({
//                        label: s.state,
//                        value: s.state,
//                      }))
//                    : stateList
//                        .filter(s => managerStates.includes(s.state.toLowerCase()))
//                        .map(s => ({
//                          label: s.state,
//                          value: s.state,
//                        }))
//                      }

//              />



//              {managerStates.length > 0 && (
//                <p className="text-xs text-slate-500 mt-1">
//                  Districts limited to manager’s assigned states
//                </p>
//              )}

//              <SearchableMultiSelect
//                label="Select District(s)"
//                items={visibleDistricts}
//                selected={form.locations}
//                disabled={loadingDistricts}

//                placeholder={
//                  loadingDistricts
//                    ? `Searching districts for ${form.selected_state}...`
//                    : "Search district"
//                }

//                onSelect={(district) => {
//                   // ❌ block adding outside manager districts
//                  if (
//                    managerLocationMode === "district" &&
//                    !managerDistricts.includes(district)
//                  ) return;

//                  if (!form.locations.includes(district)) {
//                    setForm(p => ({
//                      ...p,
//                      locations: [...p.locations, district],
//                    }));
//                  }
//                }}

//                onRemove={(district) =>
//                  setForm(p => ({
//                    ...p,
//                    locations: p.locations.filter(d => d !== district),
//                  }))
//                }
//              />

//              {managerLocationMode === "district" && (
//                <p className="text-xs text-slate-500 mt-1">
//                  Districts inherited from manager. You can add/remove only these districts.
//                </p>
//              )}

//              {managerLocationMode === "state" && (
//                <p className="text-xs text-slate-500 mt-1">
//                  Select any district from the chosen state.
//                </p>
//              )}
//            </>
//          )}
//             </div>
//           )}

//           {form.position === "EXECUTIVE" && (
//             <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
//               {/* Handle (read-only) */}
//               <CategoryGuard blocked={!isCategorySelected}>
//                 <MultiSelectChips
//                   label="Handle"
//                   required
//                   value={form.handle}
//                   disabled
//                   items={HANDLES.map(h => ({ label: h, value: h }))}
//                 />
//               </CategoryGuard>

//               {/* Manager Assigned States */}
//               <div>
//                 <label className="block text-sm font-medium text-slate-700 mb-1">
//                   Manager Assigned States
//                 </label>
//                 <SelectedTags items={managerStates} />
//               </div>

//               {/* Select State */}
//               <SelectField
//                 label="Select State"
//                 value={form.selected_state}
//                 onChange={(val) => {
//                   setForm(p => ({
//                     ...p,
//                     selected_state: val,
//                     locations: [],
//                   }));
//                   fetchDistricts(val);
//                 }}
//                 options={stateList
//                   .filter(s => managerStates.includes(s.state.toLowerCase()))
//                   .map(s => ({
//                     label: s.state,
//                     value: s.state,
//                   }))}
//               />
//             </div>
//             )}

//         {/* ---------------- LOCATION MODE ---------------- */}
//         {/* {managerStates.length === 0 && (
//           <CategoryGuard disabled={!isCategorySelected}>
//           <SelectField
//             label="Location Mode"
//             value={form.location_mode}
//             error={errors.location_mode}
//             disabled={!isCategorySelected}   // 🔒 DISABLED
//             onChange={(val) =>
//               setForm(prev => ({
//                 ...prev,
//                 location_mode: val,
//                 selected_state: "",
//                 locations: [],
//                 pincode: "",
//               }))
//             }
//             options={[
//               { label: "State Only", value: "state" },
//               { label: "State → District", value: "district" },
//               { label: "Pincode", value: "pincode" },
//             ]}
//           />
//           </CategoryGuard>
//         )} */}

//         {/* {managerStates.length > 0 && (
//           <div>
//             <label className="block text-sm font-medium text-slate-700 mb-1">
//               Manager Assigned States
//             </label>

//             <SelectedTags
//               items={managerStates}
//               readOnly
//             />
//           </div>
//         )} */}


//         {/* {isManager && (form.location_mode === "state" && managerStates.length === 0) && (
//           <>
//             <SelectField
//               label="Select State"
//               onChange={(val) => {
//                 if (!form.locations.includes(val)) {
//                   setForm(p => ({
//                     ...p,
//                     locations: [...p.locations, val],
//                   }));
//                 }
//               }}
//               options={stateList.map(s => ({
//                 label: s.state,
//                 value: s.state,
//               }))}
//             />

//             <SelectedTags
//               items={form.locations}
//               onRemove={(state) =>
//                 setForm(p => ({
//                   ...p,
//                   locations: p.locations.filter(s => s !== state),
//                 }))
//               }
//             />
//           </>
//         )} */}

        



        

//         {/* ---------------- STATE MODE ---------------- */}
//         {form.location_mode === "pincode" && (
//           <>
//             <InputField
//               label="Pincode"
//               value={form.pincode}
//               placeholder="Enter 6 digit pincode"
//               onChange={(val) => {
//                 if (val.length === 6) fetchFromPincode(val);
//                 setForm((p) => ({ ...p, pincode: val }));
//               }}
//             />


//                   {form.locations.length > 0 && (
//               <div className="mt-2 space-y-2">
//                 {form.locations.map((l) => (
//                   <div
//                     key={l.pincode}
//                     className="flex justify-between items-center bg-blue-50 border p-2 rounded text-sm"
//                   >
//                     <span>
//                       {l.pincode} – {l.district}, {l.state}
//                     </span>
//                     <button
//                       onClick={() =>
//                         setForm((p) => ({
//                           ...p,
//                           locations: p.locations.filter(
//                             (x) => x.pincode !== l.pincode
//                           ),
//                         }))
//                       }
//                       className="text-red-600 font-bold"
//                     >
//                       ×
//                     </button>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </>
//         )}


//         {/* ---------------- PINCODE MODE ---------------- */}
//         {/* {form.location_mode === "pincode" && (
//           <>
//             <InputField
//               label="Pincode"
//               value=""
//               placeholder="Enter 6 digit pincode"
//               onChange={(val) => {
//                 if (val.length === 6) fetchStateDistrictFromPin(val);
//               }}
//             />

//             {loadingPin && (
//               <p className="text-xs text-blue-500">Checking pincode...</p>
//             )}

//             {form.pinned_state && (
//               <div className="text-sm bg-blue-50 border p-2 rounded">
//                 <p><strong>State:</strong> {form.pinned_state}</p>
//                 <p><strong>District:</strong> {form.pinned_district}</p>
//               </div>
//             )}
//           </>
//         )} */}

//         {/* ---------------- ACTIONS ---------------- */}
//         <div className="flex justify-end gap-2 pt-4">
//           <BlueButton type="button" onClick={() => navigate("/allemployees")}>
//             Cancel
//           </BlueButton>
//          <BlueButton
//             type="submit"
//             disabled={submitting || uploadLoading}
//           >
//             {submitting
//               ? isEdit
//                 ? "Updating..."
//                 : "Saving..."
//               : isEdit
//               ? "Update Employee"
//               : "Save Employee"}
//           </BlueButton>
//         </div>
//       </form>
//     </div>
//   );
// }


// function SelectedTags({ items = [], onRemove }) {
//   if (!Array.isArray(items) || items.length === 0) return null;

//   return (
//     <div className="flex flex-wrap gap-2 m-3 py-2">
//       {items.map((item) => (
//         <span
//           key={item}
//           className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs flex gap-2 items-center"
//         >
//           {item}
//           <button
//             type="button"
//             onClick={() => onRemove?.(item)} // ✅ SAFE CALL
//             className="text-red-600 font-bold"
//           >
//             ×
//           </button>
//         </span>
//       ))}
//     </div>
//   );
// }


// ***************************************original working code****************
// import { useEffect, useState } from "react";
// import axios from "axios";
// import Swal from "sweetalert2";
// import { useNavigate } from "react-router-dom";

// import BlueButton from "../../../Component/Common/BlueButton";
// import SelectField from "../../../Component/RetailerComponents/SelectField";
// import InputField from "../../../Component/RetailerComponents/InputField";
// import MultiSelectChips from "../../../Component/RetailerComponents/MultiSelectChips";
// import { BASE_URL } from "../../../config/api";
// import toast from "react-hot-toast";
// import SearchableMultiSelect from "./SearchableMultiSelect";


// function CategoryGuard({
//   blocked,
//   tooltip = "Please select Category first",
//   children,
// }) {
//   return (
//     <div className="relative group">
//       {/* TOOLTIP */}
//       {blocked && (
//         <div className="
//           pointer-events-none
//           absolute -top-9 left-1/2 -translate-x-1/2
//           whitespace-nowrap
//           rounded-md bg-slate-900 px-3 py-1
//           text-xs text-white
//           opacity-0 group-hover:opacity-100
//           transition
//           z-20
//         ">
//           {tooltip}
//           <span className="
//             absolute left-1/2 top-full -translate-x-1/2
//             border-4 border-transparent border-t-slate-900
//           " />
//         </div>
//       )}

//       {/* CLICK / HOVER BLOCKER */}
//       {blocked && (
//         <div className="absolute inset-0 z-10 cursor-not-allowed" />
//       )}

//       {/* FIELD */}
//       <div className={blocked ? "opacity-60" : ""}>
//         {children}
//       </div>
//     </div>
//   );
// }


// export default function EmployeeForm({ managers: parentManagers, employee, onSubmit, onClose }) {

//   const [managers, setManagers] = useState(parentManagers || []);
//   const navigate = useNavigate();
  
//   const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);
//   const [selectedDistrictText, setSelectedDistrictText] = useState("");
//   const [loadingPin, setLoadingPin] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const [loadingCategories, setLoadingCategories] = useState(false);
//   const [uploadLoading, setUploadLoading] = useState(false);
//   const [photoUrl, setPhotoUrl] = useState("");
//   // const [managers, setManagers] = useState([]);
//   const [loadingManagers, setLoadingManagers] = useState(false);
//   const [managerStates, setManagerStates] = useState([]);
//   const [isManagerDistrictLocked, setIsManagerDistrictLocked] = useState(false);
//   const [managerDistricts, setManagerDistricts] = useState([]);
//   const [managerLocationMode, setManagerLocationMode] = useState(null);
//   const [loadingDistricts, setLoadingDistricts] = useState(false);
//   const [submitting, setSubmitting] = useState(false);
//   const isEdit = Boolean(employee?.id);
//   const initialFormState = {
//     employee_type: "",
//     position: "",
//     first_name: "",
//     middle_name: "",
//     last_name: "",
//     phone_personal: "",
//     phone_official: "",
//     official_email: "",
//     employee_id: "",
//     photo: null,

//     category: [],
//     user_type: "",
//     handle: [],

//     report_to: "",
//     location_mode: "state",
//     selected_state: "",
//     locations: [],
//     pinned_state: "",
//     pinned_district: "",
//     // password: 123456,
//   };

//   const [form, setForm] = useState(initialFormState);
//   const isCategorySelected = form.category.length > 0;
//   const isManager = form.position === "MANAGER";

//   const sessionUser = JSON.parse(localStorage.getItem("authUserData"));
//   const companyId = sessionUser?.id;

// useEffect(() => {
//   if (!employee) return;

//   const position =
//     employee.position?.toUpperCase() === "MANAGER"
//       ? "MANAGER"
//       : "EXECUTIVE";

//   const locationMode = employee.location_mode || "state";

//   setForm(prev => ({
//     ...prev,

//     employee_type: employee.employee_type
//       ? employee.employee_type.toLowerCase()
//       : "",
//     position: employee.position
//       ? employee.position.toUpperCase()
//       : "",

//     first_name: employee.first_name || "",
//     middle_name: employee.middle_name || "",
//     last_name: employee.last_name || "",

//     phone_personal: employee.personal_phone || "",
//     phone_official: employee.official_phone || "",
//     official_email: employee.official_email || "",

//     user_type: employee.type_of_user || "",
//     report_to: Number(employee.reports_to) || "",

//     category: employee.categories || [],
//     handle: employee.handle || [],

//     location_mode: locationMode,

//     selected_state:
//       locationMode === "district" || locationMode === "state"
//         ? employee.state || ""
//         : "",

//     locations:
//       locationMode === "district"
//         ? employee.district?.split(",").map(d => d.trim()) || []
//         : locationMode === "state"
//         ? employee.state?.split(",").map(s => s.trim()) || []
//         : employee.pincodes?.map(p => ({
//             pincode: p,
//             state: employee.state,
//             district: employee.district,
//           })) || [],
//   }));

//   setPhotoUrl(employee.photo_url || "");
// }, [employee]);


//   useEffect(() => {
//     if (!isEdit) return;
//     if (!employee?.reports_to) return;
//     if (managers.length === 0) return;

//     setForm(prev => ({
//       ...prev,
//       report_to: Number(employee.reports_to),
//     }));
//   }, [managers, isEdit, employee]);


//   const handleImageUpload = async (e) => {
//   const file = e.target.files[0];
//   if (!file) return;

//   setUploadLoading(true);

//   const formData = new FormData();
//   formData.append("file", file);
//   formData.append("tag", "Employee");

//   try {
//     const res = await fetch(`${BASE_URL}warranty/upload-file`, {
//       method: "POST",
//       body: formData,
//     });

//     const data = await res.json();

//     if (data?.data?.file_url) {
//       setPhotoUrl(data.data.file_url);
//       toast.success("Image uploaded");
//     }
//   } catch {
//     toast.error("Image upload failed");
//   } finally {
//     setUploadLoading(false);
//   }
// };

//   const [errors, setErrors] = useState({});
//   const [districtList, setDistrictList] = useState([]);

//   const updateField = (field, value) => {
//     setForm((prev) => ({
//       ...prev,
//       [field]: value,
//       ...(field === "position" && value === "MANAGER"
//         ? { report_to: "" }
//         : {}),
//     }));
//   };

//    function toggleMultiSelect(name, value) {
//     setForm(prev => {
//       const list = new Set(prev[name]);
//       list.has(value) ? list.delete(value) : list.add(value);
//       return { ...prev, [name]: [...list] };
//     });
//   }

//   /* ---------------- STATES ---------------- */
//   const stateList = [
//     { code: "AP", state: "ANDHRA PRADESH" },
//     { code: "TG", state: "TELANGANA" },
//     { code: "AS", state: "ASSAM" },
//     { code: "BR", state: "BIHAR" },
//     { code: "CT", state: "CHHATTISGARH" },
//     { code: "DL", state: "DELHI" },
//     { code: "GA", state: "GOA" },
//     { code: "GJ", state: "GUJARAT" },
//     { code: "HR", state: "HARYANA" },
//     { code: "HP", state: "HIMACHAL PRADESH" },
//     { code: "JK", state: "JAMMU AND KASHMIR" },
//     { code: "JH", state: "JHARKHAND" },
//     { code: "KA", state: "KARNATAKA" },
//     { code: "KL", state: "KERALA" },
//     { code: "MP", state: "MADHYA PRADESH" },
//     { code: "MH", state: "MAHARASHTRA" },
//     { code: "OD", state: "ODISHA" },
//     { code: "PB", state: "PUNJAB" },
//     { code: "RJ", state: "RAJASTHAN" },
//     { code: "TN", state: "TAMIL NADU" },
//     { code: "UP", state: "UTTAR PRADESH" },
//     { code: "UK", state: "UTTARAKHAND" },
//     { code: "WB", state: "WEST BENGAL" },
//   ];

//   const HANDLES = ["AGENT", "RETAILER", "END_USER"];

//   useEffect(() => {
//     async function fetchCategories() {
//       try {
//         setLoadingCategories(true);
//         const res = await fetch(`${BASE_URL}warranty/get-categories`);
//         const data = await res.json();
//         if (data) {
//           const active = data.filter(cat => cat.status === 1);
//           setCategories(active);
//         }
//       } catch (error) {
//         console.error("Category fetch error", error);
//         toast.error("Failed to load categories");
//       } finally {
//         setLoadingCategories(false);
//       }
//     }
//     fetchCategories();
//   }, []);

//   useEffect(() => {
//   if (!isCategorySelected) return;
//   if (!form.user_type) return;

//   async function fetchManagers() {
//     try {
//       setLoadingManagers(true);

//       const res = await axios.post(
//         `${BASE_URL}company/employee/all`,
//         {
//           company_id: companyId,
//           type_of_user: form.user_type, // 🔥 Sales / Operation
//           position: "MANAGER",
//         }
//       );

//       if (res.data?.status) {
//         setManagers(res.data.data?.data || []);
//       }
//     } catch (err) {
//       toast.error("Failed to load managers");
//     } finally {
//       setLoadingManagers(false);
//     }
//   }

//   fetchManagers();
// }, [form.user_type, isCategorySelected]);


// useEffect(() => {
//   if (isEdit) return; // 🔥 THIS LINE FIXES IT
//   if (isCategorySelected) return;

//   setForm(prev => ({
//     ...prev,
//     user_type: "",
//     position: "",
//     report_to: "",
//     handle: [],
//   }));

//   setManagers([]);
// }, [isCategorySelected, isEdit]);


// //   useEffect(() => {
// //   if (form.position !== "EXECUTIVE") return;

// //   async function fetchManagers() {
// //     try {
// //       setLoadingManagers(true);

// //       const sessionUser = JSON.parse(localStorage.getItem("authUserData"));
// //       const companyId = sessionUser?.id;

// //       const res = await axios.post(
// //         `${BASE_URL}company/employee/all?position=MANAGER`,
// //         { company_id: companyId }
// //       );

// //       if (res.data?.status) {
// //         // ✅ ALWAYS ARRAY
// //         setManagers(res.data.data?.data || []);
// //       }
// //     } catch (err) {
// //       toast.error("Failed to load managers");
// //     } finally {
// //       setLoadingManagers(false);
// //     }
// //   }

// //   fetchManagers();
// // }, [form.position]);

//   const resetManagerDerivedData = () => {
//     setManagerStates([]);
//     setDistrictList([]);

//     setForm(prev => ({
//       ...prev,
//       location_mode: "state",
//       selected_state: "",
//       locations: [],
//     }));
//   };

//   const handleManagerSelect = (managerId) => {
//   const manager = managers.find(m => m.id === Number(managerId));
//   if (!manager) return;

//   // 🔥 AUTO HANDLE INHERIT
//   const managerHandles = Array.isArray(manager.handle)
//     ? manager.handle
//     : [];

//   // reset everything first
//   setManagerStates([]);
//   setManagerDistricts([]);
//   setDistrictList([]);

//   setManagerLocationMode(manager.location_mode);

//   setForm(prev => ({
//     ...prev,
//     report_to: manager.id,
//     handle: managerHandles, // ✅ AUTO SET HANDLE
//   }));

//   // ---------- STATE MODE ----------
//   if (manager.location_mode === "state") {
//     const states = manager.state
//       ? manager.state.split(",").map(s => s.trim().toLowerCase())
//       : [];

//     setManagerStates(states);

//     setForm(prev => ({
//       ...prev,
//       location_mode: "district",
//       selected_state: "",
//       locations: [],
//     }));

//     return;
//   }

//   // ---------- DISTRICT MODE ----------
//   if (manager.location_mode === "district") {
//     const districts = manager.district
//       ? manager.district.split(",").map(d => d.trim())
//       : [];

//     setManagerStates([manager.state.toLowerCase()]);
//     setManagerDistricts(districts);

//     setForm(prev => ({
//       ...prev,
//       location_mode: "district",
//       selected_state: manager.state,
//       locations: districts,
//     }));

//     fetchDistricts(manager.state);
//   }
// };

//   /* ---------------- FETCH DISTRICTS ---------------- */
//   const fetchDistricts = async (stateName) => {
//     try {
//       setLoadingDistricts(true);      // 🔥 start loader
//       setDistrictList([]);

//       const res = await axios.get(
//         `https://goelectronix.in/api/v1/org_user/statedistrict?state=${stateName}`
//       );

//       setDistrictList(res.data || []);
//     } catch {
//       Swal.fire("Error", "Failed to fetch districts", "error");
//     } finally {
//       setLoadingDistricts(false);     // 🔥 stop loader
//     }
//   };

//   const visibleDistricts =
//   managerLocationMode === "district"
//     ? districtList
//         .map(d => d.district)
//         .filter(d => managerDistricts.includes(d))
//     : districtList.map(d => d.district);


//   /* ---------------- PINCODE LOOKUP ---------------- */
//  async function fetchFromPincode(pin) {
//   const res = await axios.get(
//     `https://goelectronix.in/public/api/v1/pincode/${pin}`
//   );

//   if (!res.data?.success) return;

//   setForm((prev) => {
//     const exists = prev.locations.some(
//       (l) => l.pincode === pin
//     );

//     if (exists) {
//       toast.error("Pincode already selected");
//       return prev;
//     }

//     return {
//       ...prev,
//       locations: [
//         ...prev.locations,
//         {
//           pincode: pin,
//           state: res.data.state,
//           district: res.data.district,
//         },
//       ],
//       pincode: "",
//     };
//   });
// }

//   const validateForm = () => {
//   const newErrors = {};

//   // BASIC
//   if (!form.employee_type) newErrors.employee_type = "Employee type is required";
//   if (!form.first_name?.trim()) newErrors.first_name = "First name is required";
//   if (!form.last_name?.trim()) newErrors.last_name = "Last name is required";

//   // PHONES
//   if (form.phone_personal.length !== 10)
//     newErrors.phone_personal = "Personal phone must be 10 digits";

//   if (form.phone_official.length !== 10)
//     newErrors.phone_official = "Official phone must be 10 digits";

//   if (
//     form.phone_personal &&
//     form.phone_official &&
//     form.phone_personal === form.phone_official
//   ) {
//     newErrors.phone_official = "Personal and official phone cannot be same";
//   }

//   // EMAIL
//   if (
//     !form.official_email ||
//     !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.official_email)
//   ) {
//     newErrors.official_email = "Valid official email required";
//   }

//   // CATEGORY
//   if (!form.category.length)
//     newErrors.category = "Select at least one category";

//   // USER FLOW
//   if (!form.user_type)
//     newErrors.user_type = "User type is required";

//   if (!form.position)
//     newErrors.position = "Position is required";

//   if (form.position === "EXECUTIVE" && !form.report_to)
//     newErrors.report_to = "Reporting manager is required";

//   if (!form.handle.length)
//     newErrors.handle = "Select at least one handle";

//   if (!form.location_mode)
//     newErrors.location_mode = "Location mode is required";

//   // LOCATION
//   if (form.location_mode === "state" && !form.locations.length)
//     newErrors.locations = "Select at least one state";

//   if (form.location_mode === "district") {
//     if (!form.selected_state)
//       newErrors.selected_state = "State is required";

//     if (!form.locations.length)
//       newErrors.locations = "Select at least one district";
//   }

//   if (form.location_mode === "pincode" && !form.locations.length)
//     newErrors.locations = "Add at least one pincode";

//   setErrors(newErrors);
//   return Object.keys(newErrors).length === 0;
// };

//   /* ---------------- SUBMIT ---------------- */
//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!validateForm()) {
//       toast.error("Please fix the errors before submitting");
//       return;
//     }

//     if (submitting) return;
//     setSubmitting(true);

//   try {
//     const sessionUser = JSON.parse(localStorage.getItem("authUserData"));
//     const companyId = sessionUser?.id;

//     if (!companyId) {
//       toast.error("Company not found");
//       return;
//     }

//     // ---------------- LOCATION PAYLOAD ----------------
//     let state = "";
//     let district = "";
//     let pincodes = [];

//     if (form.location_mode === "state") {
//       state = form.locations.join(",");
//     }

//     if (form.location_mode === "district") {
//       state = form.selected_state;
//       district = form.locations.join(",");
//     }

//     if (form.location_mode === "pincode") {
//       pincodes = form.locations.map((l) => l.pincode);
//     }

//     const payload = {

      
//       company_id: companyId,
//       first_name: form.first_name,
//       middle_name: form.middle_name,
//       last_name: form.last_name,
//       personal_phone: form.phone_personal,
//       official_phone: form.phone_official,
//       official_email: form.official_email,
//       type_of_user: form.user_type,
//       position: form.position,
//       reports_to: form.position === "EXECUTIVE" ? form.report_to : null,
//       categories: form.category,
//       handle: form.handle,
//       pincodes,
//       state,
//       district,
//       location_mode: form.location_mode,
//       password: "123456",
//       photo_url: photoUrl || "",
//     };

//     if (isEdit) {
//       await axios.post(
//         `${BASE_URL}company/employee/update/${employee.id}`,
//         payload
//       );
//       toast.success("Employee updated successfully");

//       if (!isEdit) {
//         // 🔥 Clear form
//         setForm(initialFormState);
//         setPhotoUrl("");

//         // 🔥 Clear manager-related derived state
//         setManagerStates([]);
//         setManagerDistricts([]);
//         setManagerLocationMode(null);
//         setDistrictList([]);
//       }

//     } else {
//       await axios.post(
//         `${BASE_URL}company/employee/store`,
//         payload
//       );
//       toast.success("Employee created successfully");
//       if (!isEdit) {
//         // 🔥 Clear form
//         setForm(initialFormState);
//         setPhotoUrl("");

//         // 🔥 Clear manager-related derived state
//         setManagerStates([]);
//         setManagerDistricts([]);
//         setManagerLocationMode(null);
//         setDistrictList([]);
//       }
//     }

//     onSubmit?.();
//     onClose?.();

//   } catch (error) {
//     console.error(error);
//     toast.error(
//       error?.response?.data?.message || "Failed to save employee"
//     );
//   } finally {
//     setSubmitting(false); // 🔓 ALWAYS unlock
//   }
// };

// useEffect(() => {
//   if (managerStates.length === 1) {
//     setForm(p => ({ ...p, selected_state: managerStates[0] }));
//     fetchDistricts(managerStates[0]);
//   }
// }, [managerStates]);

// useEffect(() => {

//   if (isEdit) return; 
//   // 🔥 When position changes OR cleared
//   if (!form.position) {
//     // hard reset when cleared
//   }

//   // Clear manager-derived data
//   setManagerStates([]);
//   setManagerDistricts([]);
//   setManagerLocationMode(null);
//   setDistrictList([]);

//   // Reset location-related form fields
//   setForm(prev => ({
//     ...prev,
//     report_to: "",
//     location_mode: "state",
//     selected_state: "",
//     locations: [],
//     pincode: "",
//   }));

// }, [form.position, isEdit]);

// useEffect(() => {
//   if (!isEdit || !employee || form.position !== "EXECUTIVE") return;
//   if (!employee.reports_to) return;

//   handleManagerSelect(employee.reports_to);
// }, [isEdit, employee, form.position]);

// useEffect(() => {
//   if (Array.isArray(parentManagers) && parentManagers.length > 0) {
//     setManagers(parentManagers);
//   }
// }, [parentManagers]);

//   useEffect(() => {
//   console.log("FINAL MANAGERS:", managers);
// }, [managers]);


//   return (
//     <div className="mx-auto bg-white p-6 rounded-xl shadow-sm mt-4 mb-10">
//       <h2 className="text-xl font-semibold text-[#0A3E91] mb-4">
//         {isEdit ? "Update Employee" : "Add New Employee"}
//       </h2>


//       <form className="space-y-4" onSubmit={handleSubmit}>
//         {/* ---------------- BASIC DETAILS ---------------- */}
//         <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
//           <SelectField
//             label="Employee Type"
//             required
//             value={form.employee_type}
//             error={errors.employee_type}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, employee_type: val }))
//             }
//             options={[
//               { label: "Field", value: "field" },
//               { label: "Inhouse", value: "inhouse" },
//               { label: "Inhouse TC", value: "inhouse_tc" },
//             ]}
//           />

//           <InputField
//             label="First Name"
//             required
//             value={form.first_name}
//             error={errors.first_name}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, first_name: val }))
//             }
//           />

//           <InputField
//             label="Middle Name"
//             value={form.middle_name}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, middle_name: val }))
//             }
//           />

//           <InputField
//             label="Last Name"
//             required
//             value={form.last_name}
//             error={errors.last_name}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, last_name: val }))
//             }
//           />

//           <InputField
//             label="Personal Phone"
//             value={form.phone_personal}
//             error={errors.phone_personal}
//             inputMode="numeric"        // 📱 mobile keypad
//             pattern="[0-9]*"
//             maxLength={10}             // 🔒 max 10 digits
//             onChange={(val) => {
//               const digitsOnly = val.replace(/\D/g, ""); // remove non-numbers
//               if (digitsOnly.length <= 10) {
//                 setForm((p) => ({ ...p, phone_personal: digitsOnly }));
//               }
//             }}
//           />

//           <InputField
//             label="Official Phone"
//             value={form.phone_official}
//             error={errors.phone_official}
//             inputMode="numeric"        // 📱 mobile keypad
//             pattern="[0-9]*"
//             maxLength={10}             // 🔒 max 10 digits
//             onChange={(val) => {
//               const digitsOnly = val.replace(/\D/g, ""); // allow numbers only
//               if (digitsOnly.length <= 10) {
//                 setForm((p) => ({ ...p, phone_official: digitsOnly }));
//               }
//             }}
//           />

//           <InputField
//             label="Official Email"
//             type="email"
//             value={form.official_email}
//             error={errors.official_email}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, official_email: val }))
//             }
//           />

//           {/* <InputField
//             label="Employee ID"
//             value={form.employee_id}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, employee_id: val }))
//             }
//           /> */}

//           <InputField
//             label="Employee Photo"
//             type="file"
//             helper={uploadLoading ? "Uploading..." : ""}
//             onChange={handleImageUpload}
//           />


//           <MultiSelectChips
//             label="Category"
//             required
//             loading={loadingCategories}
//             value={form.category}
//             error={errors.category}
//             onToggle={(val) => toggleMultiSelect("category", val)}
//             items={categories.map((cat) => ({
//               label: cat.name,
//               value: cat.name,
//             }))}
//           />

//         <CategoryGuard blocked={!isCategorySelected}>
//           <SelectField
//               label="User Type"
//               value={form.user_type}
//               error={errors.user_type}
//               // disabled={!isCategorySelected}
//               onChange={(val) =>
//                 setForm((p) => ({ ...p, user_type: val }))
//               }
//               options={[
//                 { label: "Sales", value: "Sales" },
//                 { label: "Operation", value: "Operation" },
//               ]}
//             />
//         </CategoryGuard>

//         <CategoryGuard blocked={!isCategorySelected}>
//           <SelectField
//             label="Position"
//             required
//             value={form.position}
//             error={errors.position}
//             // disabled={!isCategorySelected}
//             onChange={(val) => updateField("position", val)}
//             options={[
//               { label: "Manager", value: "MANAGER" },
//               { label: "Executive", value: "EXECUTIVE" },
//             ]}
//           />
//         </CategoryGuard>


//         <CategoryGuard blocked={!isCategorySelected}>
//           {form.position === "EXECUTIVE" && (
//             <SelectField
//               label="Report To"
//               required
//               value={form.report_to}
//               error={errors.report_to}
//               disabled={!isCategorySelected || loadingManagers}
//               onChange={handleManagerSelect}
//               helper={
//                 loadingManagers
//                   ? "Loading managers..."
//                   : managers.length === 0
//                   ? "No managers available"
//                   : ""
//               }
//               options={
//                 loadingManagers
//                   ? []
//                   : managers.map(m => ({
//                       label: `${m.first_name} ${m.last_name}`,
//                       value: m.id,
//                     }))
//               }
//             />
//           )}
//         </CategoryGuard>

//         <CategoryGuard disabled={!isCategorySelected}>
//           <MultiSelectChips
//             label="Handle"
//             required
//             value={form.handle}
//             error={errors.handle}
//             disabled={!isCategorySelected}   // 🔒 DISABLED
//             onToggle={(val) => {
//               if (!isCategorySelected) return;
//               if (form.position === "EXECUTIVE") return; // existing lock
//               toggleMultiSelect("handle", val);
//             }}
//             items={HANDLES.map((h) => ({
//               label: h,
//               value: h,
//             }))}
//           />
//           </CategoryGuard>
//         </div>

//         {/* ---------------- LOCATION MODE ---------------- */}
//         {managerStates.length === 0 && (
//           <CategoryGuard disabled={!isCategorySelected}>
//           <SelectField
//             label="Location Mode"
//             value={form.location_mode}
//             error={errors.location_mode}
//             disabled={!isCategorySelected}   // 🔒 DISABLED
//             onChange={(val) =>
//               setForm(prev => ({
//                 ...prev,
//                 location_mode: val,
//                 selected_state: "",
//                 locations: [],
//                 pincode: "",
//               }))
//             }
//             options={[
//               { label: "State Only", value: "state" },
//               { label: "State → District", value: "district" },
//               { label: "Pincode", value: "pincode" },
//             ]}
//           />
//           </CategoryGuard>
//         )}

//         {managerStates.length > 0 && (
//           <div>
//             <label className="block text-sm font-medium text-slate-700 mb-1">
//               Manager Assigned States
//             </label>

//             <SelectedTags
//               items={managerStates}
//               readOnly
//             />
//           </div>
//         )}


//         {isManager && (form.location_mode === "state" && managerStates.length === 0) && (
//           <>
//             <SelectField
//               label="Select State"
//               onChange={(val) => {
//                 if (!form.locations.includes(val)) {
//                   setForm(p => ({
//                     ...p,
//                     locations: [...p.locations, val],
//                   }));
//                 }
//               }}
//               options={stateList.map(s => ({
//                 label: s.state,
//                 value: s.state,
//               }))}
//             />

//             <SelectedTags
//               items={form.locations}
//               onRemove={(state) =>
//                 setForm(p => ({
//                   ...p,
//                   locations: p.locations.filter(s => s !== state),
//                 }))
//               }
//             />
//           </>
//         )}


//         {form.location_mode === "district" && (
//           <>
//             <SelectField
//               label="Select State"
//               value={form.selected_state}
//               onChange={(val) => {
//                 setForm(p => ({
//                   ...p,
//                   selected_state: val,
//                   locations: [],
//                 }));
//                 fetchDistricts(val);
//               }}
//               options={
//                 form.position === "MANAGER"
//                   ? stateList.map(s => ({
//                       label: s.state,
//                       value: s.state,
//                     }))
//                   : stateList
//                       .filter(s => managerStates.includes(s.state.toLowerCase()))
//                       .map(s => ({
//                         label: s.state,
//                         value: s.state,
//                       }))
//                     }

//             />



//             {managerStates.length > 0 && (
//               <p className="text-xs text-slate-500 mt-1">
//                 Districts limited to manager’s assigned states
//               </p>
//             )}

//             <SearchableMultiSelect
//               label="Select District(s)"
//               items={visibleDistricts}
//               selected={form.locations}
//               disabled={loadingDistricts}

//               placeholder={
//                 loadingDistricts
//                   ? `Searching districts for ${form.selected_state}...`
//                   : "Search district"
//               }

//               onSelect={(district) => {
//                 // ❌ block adding outside manager districts
//                 if (
//                   managerLocationMode === "district" &&
//                   !managerDistricts.includes(district)
//                 ) return;

//                 if (!form.locations.includes(district)) {
//                   setForm(p => ({
//                     ...p,
//                     locations: [...p.locations, district],
//                   }));
//                 }
//               }}

//               onRemove={(district) =>
//                 setForm(p => ({
//                   ...p,
//                   locations: p.locations.filter(d => d !== district),
//                 }))
//               }
//             />


//             {managerLocationMode === "district" && (
//               <p className="text-xs text-slate-500 mt-1">
//                 Districts inherited from manager. You can add/remove only these districts.
//               </p>
//             )}

//             {managerLocationMode === "state" && (
//               <p className="text-xs text-slate-500 mt-1">
//                 Select any district from the chosen state.
//               </p>
//             )}


//           </>
//         )}

//         {/* ---------------- STATE MODE ---------------- */}
//         {form.location_mode === "pincode" && (
//           <>
//             <InputField
//               label="Pincode"
//               value={form.pincode}
//               placeholder="Enter 6 digit pincode"
//               onChange={(val) => {
//                 if (val.length === 6) fetchFromPincode(val);
//                 setForm((p) => ({ ...p, pincode: val }));
//               }}
//             />


//                   {form.locations.length > 0 && (
//               <div className="mt-2 space-y-2">
//                 {form.locations.map((l) => (
//                   <div
//                     key={l.pincode}
//                     className="flex justify-between items-center bg-blue-50 border p-2 rounded text-sm"
//                   >
//                     <span>
//                       {l.pincode} – {l.district}, {l.state}
//                     </span>
//                     <button
//                       onClick={() =>
//                         setForm((p) => ({
//                           ...p,
//                           locations: p.locations.filter(
//                             (x) => x.pincode !== l.pincode
//                           ),
//                         }))
//                       }
//                       className="text-red-600 font-bold"
//                     >
//                       ×
//                     </button>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </>
//         )}


//         {/* ---------------- PINCODE MODE ---------------- */}
//         {/* {form.location_mode === "pincode" && (
//           <>
//             <InputField
//               label="Pincode"
//               value=""
//               placeholder="Enter 6 digit pincode"
//               onChange={(val) => {
//                 if (val.length === 6) fetchStateDistrictFromPin(val);
//               }}
//             />

//             {loadingPin && (
//               <p className="text-xs text-blue-500">Checking pincode...</p>
//             )}

//             {form.pinned_state && (
//               <div className="text-sm bg-blue-50 border p-2 rounded">
//                 <p><strong>State:</strong> {form.pinned_state}</p>
//                 <p><strong>District:</strong> {form.pinned_district}</p>
//               </div>
//             )}
//           </>
//         )} */}

//         {/* ---------------- ACTIONS ---------------- */}
//         <div className="flex justify-end gap-2 pt-4">
//           <BlueButton type="button" onClick={() => navigate("/allemployees")}>
//             Cancel
//           </BlueButton>
//          <BlueButton
//             type="submit"
//             disabled={submitting || uploadLoading}
//           >
//             {submitting
//               ? isEdit
//                 ? "Updating..."
//                 : "Saving..."
//               : isEdit
//               ? "Update Employee"
//               : "Save Employee"}
//           </BlueButton>
//         </div>
//       </form>
//     </div>
//   );
// }


// function SelectedTags({ items = [], onRemove }) {
//   if (!Array.isArray(items) || items.length === 0) return null;

//   return (
//     <div className="flex flex-wrap gap-2 mt-2">
//       {items.map((item) => (
//         <span
//           key={item}
//           className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs flex gap-2 items-center"
//         >
//           {item}
//           <button
//             type="button"
//             onClick={() => onRemove?.(item)} // ✅ SAFE CALL
//             className="text-red-600 font-bold"
//           >
//             ×
//           </button>
//         </span>
//       ))}
//     </div>
//   );
// }


// import { useEffect, useState } from "react";
// import axios from "axios";
// import Swal from "sweetalert2";
// import { useNavigate } from "react-router-dom";

// import BlueButton from "../../../Component/Common/BlueButton";
// import SelectField from "../../../Component/RetailerComponents/SelectField";
// import InputField from "../../../Component/RetailerComponents/InputField";
// import MultiSelectChips from "../../../Component/RetailerComponents/MultiSelectChips";
// import { BASE_URL } from "../../../config/api";
// import toast from "react-hot-toast";
// import SearchableMultiSelect from "./SearchableMultiSelect";



// export default function EmployeeForm() {
//   const navigate = useNavigate();

//   const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);
//   const [selectedDistrictText, setSelectedDistrictText] = useState("");
//   const [loadingPin, setLoadingPin] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const [loadingCategories, setLoadingCategories] = useState(false);
//   const [uploadLoading, setUploadLoading] = useState(false);
//   const [photoUrl, setPhotoUrl] = useState("");
//   const [managers, setManagers] = useState([]);
//   const [loadingManagers, setLoadingManagers] = useState(false);
//   const [managerStates, setManagerStates] = useState([]);
//   const [isManagerDistrictLocked, setIsManagerDistrictLocked] = useState(false);
//   const [managerDistricts, setManagerDistricts] = useState([]);
//   const [managerLocationMode, setManagerLocationMode] = useState(null);
//   const [loadingDistricts, setLoadingDistricts] = useState(false);

//   const [form, setForm] = useState({
//   employee_type: "",
//   position: "",               // ✅ added
//   first_name: "",
//   middle_name: "",
//   last_name: "",
//   phone_personal: "",
//   phone_official: "",
//   official_email: "",
//   employee_id: "",
//   photo: null,

//   category: [],               // ✅ array
//   user_type: "",
//   handle: [],                 // ✅ array

//   report_to: "",
//   location_mode: "state",
//   selected_state: "",
//   locations: [],
//   pinned_state: "",
//   pinned_district: "",
// });

//   const handleImageUpload = async (e) => {
//   const file = e.target.files[0];
//   if (!file) return;

//   setUploadLoading(true);

//   const formData = new FormData();
//   formData.append("file", file);
//   formData.append("tag", "Employee");

//   try {
//     const res = await fetch(`${BASE_URL}warranty/upload-file`, {
//       method: "POST",
//       body: formData,
//     });

//     const data = await res.json();

//     if (data?.data?.file_url) {
//       setPhotoUrl(data.data.file_url);
//       toast.success("Image uploaded");
//     }
//   } catch {
//     toast.error("Image upload failed");
//   } finally {
//     setUploadLoading(false);
//   }
// };


//   const [errors, setErrors] = useState({});
//   const [districtList, setDistrictList] = useState([]);

//   const updateField = (field, value) => {
//     setForm((prev) => ({
//       ...prev,
//       [field]: value,
//       ...(field === "position" && value === "MANAGER"
//         ? { report_to: "" }
//         : {}),
//     }));
//   };


//    function toggleMultiSelect(name, value) {
//     setForm(prev => {
//       const list = new Set(prev[name]);
//       list.has(value) ? list.delete(value) : list.add(value);
//       return { ...prev, [name]: [...list] };
//     });
//   }

//   /* ---------------- STATES ---------------- */
//   const stateList = [
//     { code: "MH", state: "Maharashtra" },
//     { code: "GJ", state: "Gujarat" },
//     { code: "RJ", state: "Rajasthan" },
//     { code: "KA", state: "Karnataka" },
//     { code: "TL", state: "Telangana" },
//   ];

//   const HANDLES = ["AGENT", "RETAILER", "END_USER"];


//   useEffect(() => {
//     async function fetchCategories() {
//       try {
//         setLoadingCategories(true);
//         const res = await fetch(`${BASE_URL}warranty/get-categories`);
//         const data = await res.json();
//         if (data) {
//           const active = data.filter(cat => cat.status === 1);
//           setCategories(active);
//         }
//       } catch (error) {
//         console.error("Category fetch error", error);
//         toast.error("Failed to load categories");
//       } finally {
//         setLoadingCategories(false);
//       }
//     }
//     fetchCategories();
//   }, []);


//   useEffect(() => {
//   if (form.position !== "EXECUTIVE") return;

//   async function fetchManagers() {
//     try {
//       setLoadingManagers(true);

//       const sessionUser = JSON.parse(localStorage.getItem("authUserData"));
//       const companyId = sessionUser?.id;

//       const res = await axios.post(
//         "https://zoho.goelectronix.in/api/company/employee/all?position=MANAGER",
//         { company_id: companyId }
//       );

//       if (res.data?.status) {
//         setManagers(res.data.data || []);
//       }
//     } catch (err) {
//       toast.error("Failed to load managers");
//     } finally {
//       setLoadingManagers(false);
//     }
//   }

//   fetchManagers();
// }, [form.position]);

//   const resetManagerDerivedData = () => {
//     setManagerStates([]);
//     setDistrictList([]);

//     setForm(prev => ({
//       ...prev,
//       location_mode: "state",
//       selected_state: "",
//       locations: [],
//     }));
//   };


//   const handleManagerSelect = (managerId) => {
//   const manager = managers.find(m => m.id === Number(managerId));
//   if (!manager) return;

//   // reset everything first
//   setManagerStates([]);
//   setManagerDistricts([]);
//   setDistrictList([]);

//   setManagerLocationMode(manager.location_mode);

//   // ---------- STATE MODE ----------
//   if (manager.location_mode === "state") {
//     const states = manager.state
//       ? manager.state.split(",").map(s => s.trim().toLowerCase())
//       : [];

//     setManagerStates(states);

//     setForm(prev => ({
//       ...prev,
//       report_to: manager.id,
//       location_mode: "district",
//       selected_state: "",
//       locations: [],
//     }));

//     return;
//   }

//   // ---------- DISTRICT MODE ----------
//   if (manager.location_mode === "district") {
//     const districts = manager.district
//       ? manager.district.split(",").map(d => d.trim())
//       : [];

//     setManagerStates([manager.state.toLowerCase()]);
//     setManagerDistricts(districts);

//     setForm(prev => ({
//       ...prev,
//       report_to: manager.id,
//       location_mode: "district",
//       selected_state: manager.state,
//       locations: districts, // preselect manager districts
//     }));

//     fetchDistricts(manager.state);
//     return;
//   }
// };



//   /* ---------------- FETCH DISTRICTS ---------------- */
//   const fetchDistricts = async (stateName) => {
//     try {
//       setLoadingDistricts(true);      // 🔥 start loader
//       setDistrictList([]);

//       const res = await axios.get(
//         `https://goelectronix.in/api/v1/org_user/statedistrict?state=${stateName}`
//       );

//       setDistrictList(res.data || []);
//     } catch {
//       Swal.fire("Error", "Failed to fetch districts", "error");
//     } finally {
//       setLoadingDistricts(false);     // 🔥 stop loader
//     }
//   };

//   const visibleDistricts =
//   managerLocationMode === "district"
//     ? districtList
//         .map(d => d.district)
//         .filter(d => managerDistricts.includes(d))
//     : districtList.map(d => d.district);


//   /* ---------------- PINCODE LOOKUP ---------------- */
//  async function fetchFromPincode(pin) {
//   const res = await axios.get(
//     `https://goelectronix.in/public/api/v1/pincode/${pin}`
//   );

//   if (!res.data?.success) return;

//   setForm((prev) => {
//     const exists = prev.locations.some(
//       (l) => l.pincode === pin
//     );

//     if (exists) {
//       toast.error("Pincode already selected");
//       return prev;
//     }

//     return {
//       ...prev,
//       locations: [
//         ...prev.locations,
//         {
//           pincode: pin,
//           state: res.data.state,
//           district: res.data.district,
//         },
//       ],
//       pincode: "",
//     };
//   });
// }



//   /* ---------------- SUBMIT ---------------- */
//   const handleSubmit = async (e) => {
//   e.preventDefault();

//   try {
//     const sessionUser = JSON.parse(localStorage.getItem("authUserData"));
//     const companyId = sessionUser?.id;

//     if (!companyId) {
//       toast.error("Company not found");
//       return;
//     }

//     // ---------------- LOCATION PAYLOAD ----------------
//     let state = "";
//     let district = "";
//     let pincodes = [];

//     if (form.location_mode === "state") {
//       state = form.locations.join(",");
//     }

//     if (form.location_mode === "district") {
//       state = form.selected_state;
//       district = form.locations.join(",");
//     }

//     if (form.location_mode === "pincode") {
//       pincodes = form.locations.map((l) => l.pincode);
//     }

//     // ---------------- FINAL PAYLOAD ----------------
//     const payload = {
//       company_id: companyId,

//       first_name: form.first_name,
//       middle_name: form.middle_name,
//       last_name: form.last_name,

//       personal_phone: form.phone_personal,
//       official_phone: form.phone_official,
//       official_email: form.official_email,

//       type_of_user: form.user_type,
//       position: form.position,
//       reports_to: form.position === "EXECUTIVE" ? form.report_to : null,

//       categories: form.category,
//       handle: form.handle,

//       pincodes,
//       state,
//       district,

//       location_mode: form.location_mode,
//       password: "123456", // or generate dynamically
//       photo_url: photoUrl || "",
//     };

//     // ---------------- API CALL ----------------
//     await axios.post(
//       `${BASE_URL}company/employee/store`,
//       payload,
//       { headers: { "Content-Type": "application/json" } }
//     );

//     Swal.fire("Success", "Employee created successfully!", "success")
//       .then(() => navigate("/allemployees"));

//   } catch (error) {
//     console.error(error);
//     Swal.fire(
//       "Error",
//       error?.response?.data?.message || "Failed to create employee",
//       "error"
//     );
//   }
// };

// useEffect(() => {
//   if (managerStates.length === 1) {
//     setForm(p => ({ ...p, selected_state: managerStates[0] }));
//     fetchDistricts(managerStates[0]);
//   }
// }, [managerStates]);



//   return (
//     <div className="mx-auto bg-white p-6 rounded-xl shadow-sm mt-4 mb-10">
//       <h2 className="text-xl font-semibold text-[#0A3E91] mb-4">
//         Add New Employee
//       </h2>

//       <form className="space-y-4" onSubmit={handleSubmit}>
//         {/* ---------------- BASIC DETAILS ---------------- */}
//         <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
//           <SelectField
//             label="Employee Type"
//             required
//             value={form.employee_type}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, employee_type: val }))
//             }
//             options={[
//               { label: "Field", value: "field" },
//               { label: "Inhouse", value: "inhouse" },
//               { label: "Inhouse TC", value: "inhouse_tc" },
//             ]}
//           />

//           <InputField
//             label="First Name"
//             required
//             value={form.first_name}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, first_name: val }))
//             }
//           />

//           <InputField
//             label="Middle Name"
//             value={form.middle_name}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, middle_name: val }))
//             }
//           />

//           <InputField
//             label="Last Name"
//             required
//             value={form.last_name}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, last_name: val }))
//             }
//           />

//           <InputField
//             label="Personal Phone"
//             value={form.phone_personal}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, phone_personal: val }))
//             }
//           />

//           <InputField
//             label="Official Phone"
//             value={form.phone_official}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, phone_official: val }))
//             }
//           />

//           <InputField
//             label="Official Email"
//             type="email"
//             value={form.official_email}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, official_email: val }))
//             }
//           />

//           {/* <InputField
//             label="Employee ID"
//             value={form.employee_id}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, employee_id: val }))
//             }
//           /> */}

//           <InputField
//             label="Employee Photo"
//             type="file"
//             helper={uploadLoading ? "Uploading..." : ""}
//             onChange={handleImageUpload}
//           />


//           <MultiSelectChips
//             label="Category"
//             required
//             loading={loadingCategories}
//             value={form.category}
//             error={errors.category}
//             onToggle={(val) => toggleMultiSelect("category", val)}
//             items={categories.map((cat) => ({
//               label: cat.name,
//               value: cat.name,
//             }))}
//           />


//           <SelectField
//             label="User Type"
//             value={form.user_type}
//             onChange={(val) =>
//               setForm((p) => ({ ...p, user_type: val }))
//             }
//             options={[
//               { label: "Sales", value: "Sales" },
//               { label: "Operation", value: "Operation" },
//             ]}
//           />

//           <SelectField
//             label="Position"
//             required
//             value={form.position}
//             onChange={(val) =>
//               updateField("position", val)
//             }
//             options={[
//               { label: "Manager", value: "MANAGER" },
//               { label: "Executive", value: "EXECUTIVE" },
//             ]}
//           />



//           {form.position === "EXECUTIVE" && (
//             <SelectField
//               label="Report To"
//               required
//               value={form.report_to}
//               onChange={handleManagerSelect}
//               helper={loadingManagers ? "Loading managers..." : ""}
//               options={managers.map(m => ({
//                 label: `${m.first_name} ${m.last_name}`,
//                 value: m.id,
//               }))}
//             />
//           )}

//           <MultiSelectChips
//             label="Handle"
//             required
//             value={form.handle}
//             onToggle={(val) => toggleMultiSelect("handle", val)}
//             items={HANDLES.map((h) => ({
//               label: h,
//               value: h,
//             }))}
//           />

//         </div>

//         {/* ---------------- LOCATION MODE ---------------- */}
//         {managerStates.length === 0 && (
//           <SelectField
//             label="Location Mode"
//             value={form.location_mode}
//             onChange={(val) =>
//               setForm(prev => ({
//                 ...prev,
//                 location_mode: val,
//                 selected_state: "",
//                 pincode: "",
//                 locations: [],
//               }))
//             }
//             options={[
//               { label: "State Only", value: "state" },
//               { label: "State → District", value: "district" },
//               { label: "Pincode", value: "pincode" },
//             ]}
//           />
//         )}


//         {managerStates.length > 0 && (
//           <div>
//             <label className="block text-sm font-medium text-slate-700 mb-1">
//               Manager Assigned States
//             </label>

//             <SelectedTags
//               items={managerStates}
//               readOnly
//             />
//           </div>
//         )}


//         {form.location_mode === "state" && (
//           <>
//             <SelectField
//               label="Select State"
//               value={form.selected_state}
//               onChange={(val) => {
//                 setForm(p => ({
//                   ...p,
//                   selected_state: val,
//                   locations: [],   // reset districts
//                 }));
//                 fetchDistricts(val); // ✅ fetch districts of selected state
//               }}
//               options={
//                 stateList
//                   .filter(s => managerStates.includes(s.state.toLowerCase()))
//                   .map(s => ({ label: s.state, value: s.state }))
//               }
//             />




//             {form.locations.length > 0 && (
//               <SelectedTags
//                 items={form.locations}
//                 onRemove={(state) =>
//                   setForm((p) => ({
//                     ...p,
//                     locations: p.locations.filter((s) => s !== state),
//                   }))
//                 }
//               />
//             )}
//           </>
//         )}

//         {form.location_mode === "district" && (
//           <>
//             <SelectField
//               label="Select State"
//               value={form.selected_state}
//               onChange={(val) => {
//                 setForm(p => ({
//                   ...p,
//                   selected_state: val,
//                   locations: [],
//                 }));
//                 fetchDistricts(val);
//               }}
//               options={stateList
//                 .filter(s => managerStates.includes(s.state.toLowerCase()))
//                 .map(s => ({ label: s.state, value: s.state }))
//               }
//             />



//             {managerStates.length > 0 && (
//               <p className="text-xs text-slate-500 mt-1">
//                 Districts limited to manager’s assigned states
//               </p>
//             )}

//             <SearchableMultiSelect
//               label="Select District(s)"
//               items={visibleDistricts}
//               selected={form.locations}
//               disabled={loadingDistricts}

//               placeholder={
//                 loadingDistricts
//                   ? `Searching districts for ${form.selected_state}...`
//                   : "Search district"
//               }

//               onSelect={(district) => {
//                 // ❌ block adding outside manager districts
//                 if (
//                   managerLocationMode === "district" &&
//                   !managerDistricts.includes(district)
//                 ) return;

//                 if (!form.locations.includes(district)) {
//                   setForm(p => ({
//                     ...p,
//                     locations: [...p.locations, district],
//                   }));
//                 }
//               }}

//               onRemove={(district) =>
//                 setForm(p => ({
//                   ...p,
//                   locations: p.locations.filter(d => d !== district),
//                 }))
//               }
//             />


//             {managerLocationMode === "district" && (
//               <p className="text-xs text-slate-500 mt-1">
//                 Districts inherited from manager. You can add/remove only these districts.
//               </p>
//             )}

//             {managerLocationMode === "state" && (
//               <p className="text-xs text-slate-500 mt-1">
//                 Select any district from the chosen state.
//               </p>
//             )}


//           </>
//         )}

//         {/* ---------------- STATE MODE ---------------- */}
//         {form.location_mode === "pincode" && (
//           <>
//             <InputField
//               label="Pincode"
//               value={form.pincode}
//               placeholder="Enter 6 digit pincode"
//               onChange={(val) => {
//                 if (val.length === 6) fetchFromPincode(val);
//                 setForm((p) => ({ ...p, pincode: val }));
//               }}
//             />


//                   {form.locations.length > 0 && (
//               <div className="mt-2 space-y-2">
//                 {form.locations.map((l) => (
//                   <div
//                     key={l.pincode}
//                     className="flex justify-between items-center bg-blue-50 border p-2 rounded text-sm"
//                   >
//                     <span>
//                       {l.pincode} – {l.district}, {l.state}
//                     </span>
//                     <button
//                       onClick={() =>
//                         setForm((p) => ({
//                           ...p,
//                           locations: p.locations.filter(
//                             (x) => x.pincode !== l.pincode
//                           ),
//                         }))
//                       }
//                       className="text-red-600 font-bold"
//                     >
//                       ×
//                     </button>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </>
//         )}


//         {/* ---------------- PINCODE MODE ---------------- */}
//         {/* {form.location_mode === "pincode" && (
//           <>
//             <InputField
//               label="Pincode"
//               value=""
//               placeholder="Enter 6 digit pincode"
//               onChange={(val) => {
//                 if (val.length === 6) fetchStateDistrictFromPin(val);
//               }}
//             />

//             {loadingPin && (
//               <p className="text-xs text-blue-500">Checking pincode...</p>
//             )}

//             {form.pinned_state && (
//               <div className="text-sm bg-blue-50 border p-2 rounded">
//                 <p><strong>State:</strong> {form.pinned_state}</p>
//                 <p><strong>District:</strong> {form.pinned_district}</p>
//               </div>
//             )}
//           </>
//         )} */}

//         {/* ---------------- ACTIONS ---------------- */}
//         <div className="flex justify-end gap-2 pt-4">
//           <BlueButton type="button" onClick={() => navigate("/allemployees")}>
//             Cancel
//           </BlueButton>
//          <BlueButton type="submit" disabled={uploadLoading}>
//             {uploadLoading ? "Uploading..." : "Save Employee"}
//           </BlueButton>

//         </div>
//       </form>
//     </div>
//   );
// }


// function SelectedTags({ items, onRemove }) {
//   return (
//     <div className="flex flex-wrap gap-2 mt-2">
//       {items.map((item) => (
//         <span
//           key={item}
//           className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs flex gap-2 items-center"
//         >
//           {item}
//           <button
//             onClick={() => onRemove(item)}
//             className="text-red-600 font-bold"
//           >
//             ×
//           </button>
//         </span>
//       ))}
//     </div>
//   );
// }




// import { useState, useEffect } from "react";
// import axios from "axios";

// import Swal from "sweetalert2";
// import { useNavigate } from "react-router-dom";
// import BlueButton from "../../../Component/Common/BlueButton";

// export default function EmployeeForm() {
//   const navigate = useNavigate();

//   const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);
// const [selectedDistrictText, setSelectedDistrictText] = useState("");

//   const [form, setForm] = useState({
//     employee_type: "",
//     first_name: "",
//     middle_name: "",
//     last_name: "",
//     phone_personal: "",
//     phone_official: "",
//     official_email: "",
//     employee_id: "",
//     photo: null,
//     category: "",
//     user_type: "",
//     handle: "",
//     report_to: "",
//     location_mode: "state",
//     selected_state: "",
//     locations: [],
//   });

//   const [errors, setErrors] = useState({});
//   const [loadingPin, setLoadingPin] = useState(false);

//   // ✅ TEMPORARY 5 STATES LIST  
//   const [stateList] = useState([
//     { code: "MH", state: "Maharashtra" },
//     { code: "GJ", state: "Gujarat" },
//     { code: "RJ", state: "Rajasthan" },
//     { code: "KA", state: "Karnataka" },
//     { code: "TL", state: "Telangana" },
//   ]);

//   const [districtList, setDistrictList] = useState([]);

//   // ❌ REMOVED API fetchStates()

//   // Fetch districts (your existing API)
//   const fetchDistricts = async (stateName) => {
//     try {
//       const res = await axios.get(
//         `https://goelectronix.in/api/v1/org_user/statedistrict?state=${stateName}`
//       );
//       setDistrictList(res.data || []); // API returns array directly
//     } catch (err) {
//       console.log("District fetch error", err);
//     }
//   };


//   const handleChange = (e) => {
//     const { name, value, files } = e.target;

//     if (files) {
//       setForm((prev) => ({ ...prev, [name]: files[0] }));
//     } else {
//       setForm((prev) => ({ ...prev, [name]: value }));
//     }

//     setErrors((prev) => ({ ...prev, [name]: "" }));

//     if (name === "pincode" && form.location_mode === "pincode") {
//       if (value.length === 6) {
//         fetchStateDistrictFromPin(value);
//       }
//     }

//     if (name === "selected_state" && form.location_mode === "district") {
//       fetchDistricts(value);
//     }
//   };

//   const fetchStateDistrictFromPin = async (pincode) => {
//     try {
//       setLoadingPin(true);
//       const res = await axios.get(
//         `https://goelectronix.in/public/api/v1/pincode/${pincode}`
//       );

//       if (res.data?.success) {
//         const { state, district } = res.data;

//         setForm((prev) => ({
//           ...prev,
//           pinned_state: state,
//           pinned_district: district,
//         }));
//       } else {
//         Swal.fire("Invalid Pincode", "Enter correct pincode.", "error");
//       }
//     } catch {
//       Swal.fire("Error", "Failed to fetch pincode details.", "error");
//     } finally {
//       setLoadingPin(false);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     try {
//       const fd = new FormData();
//       Object.keys(form).forEach((key) => {
//         fd.append(key, form[key]);
//       });

//       await axios.post("https://goelectronix.in/api/v1/org_user", fd, {
//         headers: { "Content-Type": "multipart/form-data" },
//       });

//       Swal.fire("Success", "Employee saved!", "success").then(() =>
//         navigate("/allemployees")
//       );
//     } catch (err) {
//       Swal.fire("Error", "Unable to save employee.", "error");
//     }
//   };

//   return (
//     <div className="mx-auto bg-white p-6 rounded-xl shadow-sm mt-4 mb-10">
//       <h2 className="text-xl font-semibold text-[#0A3E91] mb-4">
//         Add New Employee
//       </h2>

//       <form  className="space-y-3">
//         <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
//           <div>
//             <select
//               name="employee_type"
//               value={form.employee_type}
//               onChange={handleChange}
//               className="rounded-lg border px-3 py-2 text-sm w-full"
//             >
//               <option value="">Select Employee Type</option>
//               <option value="field">Field</option>
//               <option value="inhouse">Inhouse</option>
//               <option value="inhouse_tc">Inhouse TC</option>
//             </select>
//           </div>

//           <div className="flex flex-col gap-1">
//             <label className="text-xs font-medium text-slate-700">
//               First Name <span className="text-red-500">*</span>
//             </label>

//             <input
//               name="first_name"
//               value={form.first_name}
//               onChange={handleChange}
//               placeholder="Enter first name"
//               className="rounded-lg border px-3 py-2 text-sm w-full focus:ring-1 focus:ring-blue-500"
//             />
//           </div>


//           <input
//             name="middle_name"
//             value={form.middle_name}
//             onChange={handleChange}
//             placeholder="Middle Name"
//             className="rounded-lg border px-3 py-2 text-sm w-full"
//           />

//           <input
//             name="last_name"
//             value={form.last_name}
//             onChange={handleChange}
//             placeholder="Last Name"
//             className="rounded-lg border px-3 py-2 text-sm w-full"
//           />

//           <input
//             name="phone_personal"
//             value={form.phone_personal}
//             onChange={handleChange}
//             placeholder="Personal Phone Number"
//             className="rounded-lg border px-3 py-2 text-sm w-full"
//           />

//           <input
//             name="phone_official"
//             value={form.phone_official}
//             onChange={handleChange}
//             placeholder="Official Phone Number"
//             className="rounded-lg border px-3 py-2 text-sm w-full"
//           />

//           <input
//             name="official_email"
//             value={form.official_email}
//             onChange={handleChange}
//             placeholder="Official Email ID"
//             className="rounded-lg border px-3 py-2 text-sm w-full"
//           />

//           <input
//             name="employee_id"
//             value={form.employee_id}
//             onChange={handleChange}
//             placeholder="Employee ID"
//             className="rounded-lg border px-3 py-2 text-sm w-full"
//           />

//           <div className="flex flex-col">
//             <input
//               type="file"
//               name="photo"
//               onChange={handleChange}
//               className="block w-full text-sm text-gray-700 border border-gray-300 rounded-md bg-white px-3 py-2"
//             />
//           </div>

//           <select
//             name="category"
//             value={form.category}
//             onChange={handleChange}
//             className="rounded-lg border px-3 py-2 text-sm w-full"
//           >
//             <option value="">Select Category</option>
//             <option value="tv">TV</option>
//             <option value="ref">Refrigerator</option>
//             <option value="mobile">Mobile</option>
//             <option value="tablet">Tablet</option>
//             <option value="iron">Iron</option>
//           </select>

//           <select
//             name="user_type"
//             value={form.user_type}
//             onChange={handleChange}
//             className="rounded-lg border px-3 py-2 text-sm w-full"
//           >
//             <option value="">Type of User</option>
//             <option value="sales">Sales</option>
//             <option value="operation">Operation</option>
//           </select>

//           <select
//             name="handle"
//             value={form.handle}
//             onChange={handleChange}
//             className="rounded-lg border px-3 py-2 text-sm w-full"
//           >
//             <option value="">Handle</option>
//             <option value="agent">Agent</option>
//             <option value="retailer">Retailer</option>
//             <option value="end_user">End User</option>
//           </select>

          // <select
          //   name="report_to"
          //   value={form.report_to}
          //   onChange={handleChange}
          //   className="rounded-lg border px-3 py-2 text-sm w-full"
          // >
          //   <option value="">Select Senior</option>
          // </select>


//          {/* LOCATION MODE */}
// <select
//   name="location_mode"
//   value={form.location_mode}
//   onChange={handleChange}
//   className="rounded-lg border px-3 py-2 text-sm w-full mb-3"
// >
//   <option value="state">State Only</option>
//   <option value="district">State → District</option>
//   <option value="pincode">Pincode</option>
// </select>

// {/* STATE MODE */}
// {/* STATE MODE */}
//           {form.location_mode === "state" && (
//             <>
//               {/* Single State Dropdown (add to list) */}
//               <select
//                 name="selected_state"
//                 value={form.selected_state}
//                 onChange={(e) => {
//                   handleChange(e);

//                   const selected = e.target.value;

//                   // Add selected state to multiple selection list
//                   if (selected && !form.locations.includes(selected)) {
//                     setForm((prev) => ({
//                       ...prev,
//                       locations: [...prev.locations, selected],
//                     }));
//                   }
//                 }}
//                 className="rounded-lg border px-3 py-2 text-sm w-full mb-3"
//               >
//                 <option value="">Select a State</option>
//                 {stateList.map((s) => (
//                   <option key={s.code} value={s.state}>
//                     {s.state}
//                   </option>
//                 ))}
//               </select>

//               {/* MULTI-SELECT BOX (no checkbox) */}
//               {/* <select
//                 name="locations"
//                 multiple
//                 className="rounded-lg border px-3 py-2 text-sm w-full h-32"
//                 value={form.locations}
//                 onChange={(e) => {
//                   const selectedStates = [...e.target.selectedOptions].map(
//                     (o) => o.value
//                   );
//                   setForm((prev) => ({ ...prev, locations: selectedStates }));
//                 }}
//               >
//                 {stateList.map((s) => (
//                   <option key={s.code} value={s.state}>
//                     {s.state}
//                   </option>
//                 ))}
//               </select> */}

//               {/* SHOW SELECTED STATES */}
//               {form.locations.length > 0 && (
//                 <div className="mt-2 text-sm bg-blue-50 border p-2 rounded">
//                   <strong>Selected States:</strong> {form.locations.join(", ")}
//                 </div>
//               )}
//             </>
//           )}

//           {/* DISTRICT MODE */}
//           {form.location_mode === "district" && (
//             <>
//               {/* Select State */}
//               <select
//                 name="selected_state"
//                 value={form.selected_state}
//                 onChange={(e) => {
//                   handleChange(e);
//                   setForm((prev) => ({ ...prev, locations: [] })); // reset old districts
//                 }}
//                 className="rounded-lg border px-3 py-2 text-sm w-full mb-3"
//               >
//                 <option value="">Select State</option>
//                 {stateList.map((s) => (
//                   <option key={s.code} value={s.state}>
//                     {s.state}
//                   </option>
//                 ))}
//               </select>

//               {/* District Dropdown (click to add) */}
//               {form.selected_state && (
//                 <div className="relative w-full mb-3">
//                   {/* Custom dropdown button */}
//                   <div
//                     onClick={() => setShowDistrictDropdown((prev) => !prev)}
//                     className="border rounded-lg px-3 py-2 text-sm bg-white cursor-pointer"
//                   >
//                     {selectedDistrictText || "Select District"}
//                   </div>

//                   {/* Dropdown list */}
//                   {showDistrictDropdown && (
//                     <div className="absolute z-10 mt-1 w-full border bg-white rounded-lg shadow"
//                         style={{ maxHeight: "160px", overflowY: "auto" }}>

//                       {districtList.map((d) => (
//                         <div
//                           key={d.id}
//                           onClick={() => {
//                             // add selected district
//                             if (!form.locations.includes(d.district)) {
//                               setForm((prev) => ({
//                                 ...prev,
//                                 locations: [...prev.locations, d.district],
//                               }));
//                             }
//                             setSelectedDistrictText(d.district);
//                             setShowDistrictDropdown(false);
//                           }}
//                           className="px-3 py-2 text-sm hover:bg-blue-100 cursor-pointer"
//                         >
//                           {d.district}
//                         </div>
//                       ))}

//                     </div>
//                   )}
//                 </div>
//               )}



//               {/* Selected District Tags */}
//               {form.locations.length > 0 && (
//                 <div className="flex flex-wrap gap-2 mt-2">
//                   {form.locations.map((district) => (
//                     <div
//                       key={district}
//                       className="px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-2"
//                     >
//                       {district}
//                       <button
//                         onClick={() =>
//                           setForm((prev) => ({
//                             ...prev,
//                             locations: prev.locations.filter((d) => d !== district),
//                           }))
//                         }
//                         className="text-red-600 font-bold"
//                       >
//                         ×
//                       </button>
//                     </div>
//                   ))}
//                 </div>
//               )}
//             </>
//           )}

//           {/* PINCODE */}
//           {form.location_mode === "pincode" && (
//             <>
//               <input
//                 type="text"
//                 maxLength={6}
//                 name="pincode"
//                 onChange={handleChange}
//                 placeholder="Enter 6-digit Pincode"
//                 className="rounded-lg border px-3 py-2 text-sm w-full mb-2"
//               />

//               {loadingPin && (
//                 <p className="text-xs text-blue-500">Checking...</p>
//               )}

//               {form.pinned_state && (
//                 <div className="text-sm mt-2 bg-blue-50 p-2 rounded border">
//                   <p>
//                     <strong>State:</strong> {form.pinned_state}
//                   </p>
//                   <p>
//                     <strong>District:</strong> {form.pinned_district}
//                   </p>
//                 </div>
//               )}
//             </>
//           )}
         
//         </div>
        
//         <div className="flex justify-end gap-2 mt-4">
//           <BlueButton type="button" onClick={() => navigate("/allemployees")}>
//             Cancel
//           </BlueButton>
//           <BlueButton type="submit">Save Employee</BlueButton>
//         </div>
//       </form>
//     </div>
//   );
// }


// **************************************************updated code****************************************************


// import { useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import toast from "react-hot-toast";
// import { BASE_URL } from "../../../config/api";

// const HANDLES = ["AGENT", "RETAILER", "END_USER"];

// export default function EmployeeForm({ managers = [], employee = null, onSubmit, onClose }) {
//   const navigate = useNavigate();

  // const [categories, setCategories] = useState([]);
  // const [loadingCategories, setLoadingCategories] = useState(false);

//   const [form, setForm] = useState({
//     firstName: "",
//     middleName: "",
//     lastName: "",
//     personalPhone: "",
//     officialPhone: "",
//     officialEmail: "",
//     employeeId: "",
//     category: [],
//     typeOfUser: "SALES",
//     handle: [],
//     reportsToId: "",
//     area: "",
//     state: "",
//     district: "",
//     pincodes: "",
//     position: "EXECUTIVE"
//   });

//   // Pre-fill form when updating
//   useEffect(() => {
//     if (employee) {
//       setForm({
//         firstName: employee.first_name || "",
//         middleName: employee.middle_name || "",
//         lastName: employee.last_name || "",
//         personalPhone: employee.personal_phone || "",
//         officialPhone: employee.official_phone || "",
//         officialEmail: employee.official_email || "",
//         employeeId: employee.employee_id || "",
//         category: employee.categories || [],
//         typeOfUser: employee.type_of_user || "SALES",
//         handle: employee.handle || [],
//         reportsToId: employee.reports_to || "",
//         area: employee.area || "",
//         state: employee.state || "",
//         district: employee.district || "",
//         pincodes: employee.pincodes ? employee.pincodes.join(", ") : "",
//         position: employee.position || "EXECUTIVE"
//       });
//     }
//   }, [employee]);

//   // Fetch categories
  // useEffect(() => {
  //   async function fetchCategories() {
  //     try {
  //       setLoadingCategories(true);
  //       const res = await fetch(`${BASE_URL}warranty/get-categories`);
  //       const data = await res.json();
  //       if (data) {
  //         const active = data.filter(cat => cat.status === 1);
  //         setCategories(active);
  //       }
  //     } catch (error) {
  //       console.error("Category fetch error", error);
  //       toast.error("Failed to load categories");
  //     } finally {
  //       setLoadingCategories(false);
  //     }
  //   }
  //   fetchCategories();
  // }, []);

//   function updateField(name, value) {
//     setForm(prev => ({ ...prev, [name]: value }));
//   }

  // function toggleMultiSelect(name, value) {
  //   setForm(prev => {
  //     const list = new Set(prev[name]);
  //     list.has(value) ? list.delete(value) : list.add(value);
  //     return { ...prev, [name]: [...list] };
  //   });
  // }

//   async function handleSubmit(e) {
//     e.preventDefault();

//     try {
//       const authUserString = localStorage.getItem("authUserData");
//       const authUser = authUserString ? JSON.parse(authUserString) : null;
//       const companyId = authUser?.id || 1;

//       // ------------------------------------
//       // ✅ UPDATE EMPLOYEE PAYLOAD (ONLY REQUIRED FIELDS)
//       // ------------------------------------
//       if (employee) {
//         const updatePayload = {
//           first_name: form.firstName,
//           official_email: form.officialEmail,
//           position: form.position,
//           categories: form.category,
//           handle: form.handle,
//           pincodes: form.pincodes
//             ? form.pincodes.split(",").map(p => p.trim())
//             : []
//         };

//         const updateUrl = `${BASE_URL}/company/employee/update/${employee.id}`;

//         const response = await fetch(updateUrl, {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify(updatePayload)
//         });

//         const result = await response.json();

//         if (result.status === true) {
//           toast.success("Employee updated successfully!");
//           if (onSubmit) onSubmit();
//           if (onClose) onClose();
//         } else {
//           toast.error(result.message || "Failed to update employee.");
//         }

//         return; // stop here (do not run create API)
//       }

//       // ------------------------------------
//       // ✅ CREATE EMPLOYEE PAYLOAD (UNCHANGED)
//       // ------------------------------------
//       const createPayload = {
//         company_id: companyId,
//         first_name: form.firstName,
//         middle_name: form.middleName,
//         last_name: form.lastName,
//         personal_phone: form.personalPhone,
//         official_phone: form.officialPhone,
//         official_email: form.officialEmail,
//         employee_id: form.employeeId,
//         type_of_user: form.typeOfUser,
//         position: form.position,
//         reports_to: Number(form.reportsToId),
//         categories: form.category,
//         handle: form.handle,
//         pincodes: form.pincodes
//           ? form.pincodes.split(",").map(p => p.trim())
//           : [],
//         state: form.state,
//         district: form.district,
//         area: form.area,
//         password: "123456"
//       };

//       const createUrl = `${BASE_URL}company/employee/store`;

//       const response = await fetch(createUrl, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(createPayload)
//       });

//       const result = await response.json();

//       if (result.status === true) {
//         toast.success("Employee created successfully!");
//         if (onSubmit) onSubmit();
//         if (onClose) onClose();

//         setForm({
//           firstName: "",
//           middleName: "",
//           lastName: "",
//           personalPhone: "",
//           officialPhone: "",
//           officialEmail: "",
//           employeeId: "",
//           category: [],
//           typeOfUser: "SALES",
//           handle: [],
//           reportsToId: "",
//           area: "",
//           state: "",
//           district: "",
//           pincodes: "",
//           position: "EXECUTIVE"
//         });
//       } else {
//         toast.error(result.message || "Failed to save employee.");
//       }

//     } catch (error) {
//       console.error("Submit Error:", error);
//       toast.error("Something went wrong! Try again.");
//     }
//   }

//   return (
//     <div className="mx-auto bg-white shadow-md rounded-2xl p-4 m-4">
//       <h2 className="text-2xl font-semibold text-primary mb-4">
//         {employee ? "Update Employee" : "Onboard Employee"}
//       </h2>

//       <form onSubmit={handleSubmit} className="space-y-6">
//         {/* Personal Info */}
//         <div>
//           <h3 className="text-lg font-semibold mb-1 text-slate-800">Personal Information</h3>
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//             <div>
//               <label className="block text-sm font-medium text-slate-700">First Name</label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.firstName}
//                 required
//                 onChange={e => updateField("firstName", e.target.value)}
//               />
//             </div>
//             <div>
//               <label className="block text-sm font-medium text-slate-700">Middle Name</label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.middleName}
//                 onChange={e => updateField("middleName", e.target.value)}
//               />
//             </div>
//             <div>
//               <label className="block text-sm font-medium text-slate-700">Last Name</label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.lastName}
//                 required
//                 onChange={e => updateField("lastName", e.target.value)}
//               />
//             </div>
//           </div>
//         </div>

//         {/* Contact Info */}
//         <div>
//           <h3 className="text-lg font-semibold mb-1 text-slate-800">Contact Details</h3>
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//             <div>
//               <label className="block text-sm font-medium text-slate-700">Personal Phone</label>
//               <input
//                 type="tel"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.personalPhone}
//                 onChange={e => updateField("personalPhone", e.target.value)}
//               />
//             </div>
//             <div>
//               <label className="block text-sm font-medium text-slate-700">Official Phone</label>
//               <input
//                 type="tel"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.officialPhone}
//                 onChange={e => updateField("officialPhone", e.target.value)}
//               />
//             </div>
//             <div>
//               <label className="block text-sm font-medium text-slate-700">Official Email</label>
//               <input
//                 type="email"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.officialEmail}
//                 onChange={e => updateField("officialEmail", e.target.value)}
//               />
//             </div>
//           </div>
//         </div>

//         {/* Employee Info */}
//         <div>
//           <h3 className="text-lg font-semibold mb-1 text-slate-800">Employee Details</h3>
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//             <div>
//               <label className="block text-sm font-medium text-slate-700">Employee ID</label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 required
//                 value={form.employeeId}
//                 onChange={e => updateField("employeeId", e.target.value)}
//               />
//             </div>
//             <div>
//               <label className="block text-sm font-medium text-slate-700">Type of User</label>
//               <select
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.typeOfUser}
//                 onChange={e => updateField("typeOfUser", e.target.value)}
//               >
//                 <option value="SALES">Sales</option>
//                 <option value="OPERATIONS">Operations</option>
//               </select>
//             </div>
//             <div>
              // <label className="block text-sm font-medium text-slate-700">Position</label>
              // <select
              //   className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              //   value={form.position}
              //   onChange={e => updateField("position", e.target.value)}
              // >
              //   <option value="MANAGER">Manager</option>
              //   <option value="EXECUTIVE">Executive</option>
              // </select>
//             </div>
//           </div>

//           {/* Category / Handle / Reports To */}
//           <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
//             {/* Category */}
//             <div>
//               <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
//               {loadingCategories ? (
//                 <p className="text-xs text-slate-500">Loading categories...</p>
//               ) : (
//                 <div className="flex flex-wrap gap-2">
//                   {categories.map(cat => {
//                     const active = form.category.includes(cat.name);
//                     return (
//                       <button
//                         key={cat.id}
//                         type="button"
//                         onClick={() => toggleMultiSelect("category", cat.name)}
//                         className={`px-3 py-1 rounded-full text-xs border ${
//                           active
//                             ? "bg-primary text-white border-primary"
//                             : "bg-slate-50 text-slate-700 border-slate-300"
//                         }`}
//                       >
//                         {cat.name}
//                       </button>
//                     );
//                   })}
//                 </div>
//               )}
//             </div>

//             {/* Handle */}
            // <div>
            //   <label className="block text-sm font-medium text-slate-700 mb-1">Handle</label>
            //   <div className="flex flex-wrap gap-2">
            //     {HANDLES.map(h => {
            //       const active = form.handle.includes(h);
            //       return (
            //         <button
            //           key={h}
            //           type="button"
            //           onClick={() => toggleMultiSelect("handle", h)}
            //           className={`px-3 py-1 rounded-full text-xs border ${
            //             active
            //               ? "bg-primary text-white border-primary"
            //               : "bg-slate-50 text-slate-700 border-slate-300"
            //           }`}
            //         >
            //           {h}
            //         </button>
            //       );
            //     })}
            //   </div>
            // </div>

//             {/* Reports To */}
//             <div>
//               <label className="block text-sm font-medium text-slate-700 mb-1">Reports To</label>
//               <select
//                 className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.reportsToId}
//                 onChange={e => updateField("reportsToId", e.target.value)}
//               >
//                 <option value="">Select Manager</option>
//                 {managers.map(m => (
//                   <option key={m.id} value={m.id}>
//                     {m.first_name} {m.last_name} ({m.employee_id})
//                   </option>
//                 ))}
//               </select>
//             </div>
//           </div>
//         </div>

//         {/* Location */}
//         <div>
//           <h3 className="text-lg font-semibold mb-2 text-slate-800">Location Assignment</h3>
//           <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
//             <div>
//               <label className="block text-sm font-medium text-slate-700">Area</label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 placeholder="Enter Area"
//                 value={form.area}
//                 onChange={e => updateField("area", e.target.value)}
//               />
//             </div>
//             <div>
//               <label className="block text-sm font-medium text-slate-700">State</label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.state}
//                 onChange={e => updateField("state", e.target.value)}
//               />
//             </div>
//             <div>
//               <label className="block text-sm font-medium text-slate-700">District</label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.district}
//                 onChange={e => updateField("district", e.target.value)}
//               />
//             </div>
//             <div>
//               <label className="block text-sm font-medium text-slate-700">Pincodes (comma separated)</label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 placeholder="400001, 400002"
//                 value={form.pincodes}
//                 onChange={e => updateField("pincodes", e.target.value)}
//               />
//             </div>
//           </div>
//         </div>

//         {/* Actions */}
//         <div className="flex justify-end gap-3">
//           <button
//             type="button"
//             className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50"
//             onClick={onClose || (() => navigate("/allemployees"))}
//           >
//             Cancel
//           </button>
//           <button
//             type="submit"
//             className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary-dark"
//           >
//             {employee ? "Update Employee" : "Create Employee"}
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// }




// import { useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import toast from "react-hot-toast";

// const HANDLES = ["AGENT", "RETAILER", "END_USER"];

// export default function EmployeeForm({ managers = [], onSubmit }) {
//   const navigate = useNavigate();

//   const [categories, setCategories] = useState([]);
//   const [loadingCategories, setLoadingCategories] = useState(false);
//   const [form, setForm] = useState({
//     firstName: "",
//     middleName: "",
//     lastName: "",
//     personalPhone: "",
//     officialPhone: "",
//     officialEmail: "",
//     employeeId: "",
//     category: [],
//     typeOfUser: "SALES",
//     handle: [],
//     reportsToId: "",
//     area: "",
//     state: "",
//     district: "",
//     pincodes: "",
//     position: "EXECUTIVE"
//   });

//   useEffect(() => {
//     async function fetchCategories() {
//       try {
//         setLoadingCategories(true);
//         const res = await fetch("https://goelectronix.in/api/v1/warranty/get-categories");
//         const data = await res.json();
//         if (data) {
//           const active = data.filter(cat => cat.status === 1);
//           setCategories(active);
//         }
//       } catch (error) {
//         console.error("Category fetch error", error);
//       } finally {
//         setLoadingCategories(false);
//       }
//     }
//     fetchCategories();
//   }, []);

//   function updateField(name, value) {
//     setForm(prev => ({ ...prev, [name]: value }));
//   }

//   function toggleMultiSelect(name, value) {
//     setForm(prev => {
//       const list = new Set(prev[name]);
//       list.has(value) ? list.delete(value) : list.add(value);
//       return { ...prev, [name]: [...list] };
//     });
//   }

//   async function handleSubmit(e) {
//     e.preventDefault();
//     try {
//       const authUserString = localStorage.getItem("authUserData");
//       const authUser = authUserString ? JSON.parse(authUserString) : null;
//       const companyId = authUser?.id || 1;

//       const payload = {
//         company_id: companyId,
//         first_name: form.firstName,
//         middle_name: form.middleName,
//         last_name: form.lastName,
//         personal_phone: form.personalPhone,
//         official_phone: form.officialPhone,
//         official_email: form.officialEmail,
//         employee_id: form.employeeId,
//         type_of_user: form.typeOfUser,
//         position: form.position,
//         reports_to: Number(form.reportsToId),
//         categories: form.category,
//         handle: form.handle,
//         pincodes: form.pincodes
//           ? form.pincodes.split(",").map(p => p.trim())
//           : [],
//         state: form.state,
//         district: form.district,
//         password: "123456" // default password
//       };

//       const response = await fetch(
//         "${BASE_URL}/company/employee/store",
//         {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify(payload),
//         }
//       );

//       const result = await response.json();

//       if (result.status === true) {
//         toast.success("Employee created successfully!");
//         setForm({
//           firstName: "",
//           middleName: "",
//           lastName: "",
//           personalPhone: "",
//           officialPhone: "",
//           officialEmail: "",
//           employeeId: "",
//           category: [],
//           typeOfUser: "SALES",
//           handle: [],
//           reportsToId: "",
//           area: "",
//           state: "",
//           district: "",
//           pincodes: "",
//           position: "EXECUTIVE"
//         });
//         if (onSubmit) onSubmit(form);
//         setTimeout(() => navigate("/allemployees"), 1000);
//       } else {
//         toast.error(result.message || "Failed to create employee.");
//       }
//     } catch (error) {
//       console.error("Submit Error:", error);
//       toast.error("Something went wrong! Try again.");
//     }
//   }

//   return (
//     <div className="mx-auto bg-white shadow-md rounded-2xl p-4 m-4">
//       <h2 className="text-2xl font-semibold text-primary mb-4">
//         Onboard Employee
//       </h2>

//       <form onSubmit={handleSubmit} className="space-y-6">
//         {/* Personal Info */}
//         <div>
//           <h3 className="text-lg font-semibold mb-1 text-slate-800">
//             Personal Information
//           </h3>
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 First Name
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.firstName}
//                 required
//                 onChange={e => updateField("firstName", e.target.value)}
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Middle Name
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.middleName}
//                 onChange={e => updateField("middleName", e.target.value)}
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Last Name
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.lastName}
//                 required
//                 onChange={e => updateField("lastName", e.target.value)}
//               />
//             </div>
//           </div>
//         </div>

//         {/* Contact Info */}
//         <div>
//           <h3 className="text-lg font-semibold mb-1 text-slate-800">
//             Contact Details
//           </h3>
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Personal Phone
//               </label>
//               <input
//                 type="tel"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.personalPhone}
//                 onChange={e => updateField("personalPhone", e.target.value)}
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Official Phone
//               </label>
//               <input
//                 type="tel"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.officialPhone}
//                 onChange={e => updateField("officialPhone", e.target.value)}
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Official Email
//               </label>
//               <input
//                 type="email"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.officialEmail}
//                 onChange={e => updateField("officialEmail", e.target.value)}
//               />
//             </div>
//           </div>
//         </div>

//         {/* Employee Info */}
//         <div>
//           <h3 className="text-lg font-semibold mb-1 text-slate-800">
//             Employee Details
//           </h3>
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Employee ID
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 required
//                 value={form.employeeId}
//                 onChange={e => updateField("employeeId", e.target.value)}
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Type of User
//               </label>
//               <select
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.typeOfUser}
//                 onChange={e => updateField("typeOfUser", e.target.value)}
//               >
//                 <option value="SALES">Sales</option>
//                 <option value="OPERATIONS">Operations</option>
//               </select>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Position
//               </label>
//               <select
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.position}
//                 onChange={e => updateField("position", e.target.value)}
//               >
//                 <option value="MANAGER">Manager</option>
//                 <option value="EXECUTIVE">Executive</option>
//               </select>
//             </div>
//           </div>

//           <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
//             {/* Category */}
//             <div>
//               <label className="block text-sm font-medium text-slate-700 mb-1">
//                 Category
//               </label>

//               {loadingCategories ? (
//                 <p className="text-xs text-slate-500">Loading categories...</p>
//               ) : (
//                 <div className="flex flex-wrap gap-2">
//                   {categories.map(cat => {
//                     const active = form.category.includes(cat.name);
//                     return (
//                       <button
//                         key={cat.id}
//                         type="button"
//                         onClick={() => toggleMultiSelect("category", cat.name)}
//                         className={`px-3 py-1 rounded-full text-xs border ${
//                           active
//                             ? "bg-primary text-white border-primary"
//                             : "bg-slate-50 text-slate-700 border-slate-300"
//                         }`}
//                       >
//                         {cat.name}
//                       </button>
//                     );
//                   })}
//                 </div>
//               )}
//             </div>

//             {/* Handle */}
//             <div>
//               <label className="block text-sm font-medium text-slate-700 mb-1">
//                 Handle
//               </label>
//               <div className="flex flex-wrap gap-2">
//                 {HANDLES.map(h => {
//                   const active = form.handle.includes(h);
//                   return (
//                     <button
//                       key={h}
//                       type="button"
//                       onClick={() => toggleMultiSelect("handle", h)}
//                       className={`px-3 py-1 rounded-full text-xs border ${
//                         active
//                           ? "bg-primary text-white border-primary"
//                           : "bg-slate-50 text-slate-700 border-slate-300"
//                       }`}
//                     >
//                       {h}
//                     </button>
//                   );
//                 })}
//               </div>
//             </div>

//             {/* Reports To */}
//             <div>
//               <label className="block text-sm font-medium text-slate-700 mb-1">
//                 Reports To
//               </label>
//               <select
//                 className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.reportsToId}
//                 onChange={e => updateField("reportsToId", e.target.value)}
//               >
//                 <option value="">Select Manager</option>
//                 {managers.map(m => (
//                   <option key={m.id} value={m.id}>
//                     {m.firstName} {m.lastName} ({m.employeeId})
//                   </option>
//                 ))}
//               </select>
//             </div>
//           </div>
//         </div>

//         {/* Location */}
//         <div>
//           <h3 className="text-lg font-semibold mb-2 text-slate-800">
//             Location Assignment
//           </h3>

//           <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Area
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 placeholder="Enter Area"
//                 value={form.area}
//                 onChange={e => updateField("area", e.target.value)}
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 State
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.state}
//                 onChange={e => updateField("state", e.target.value)}
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 District
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.district}
//                 onChange={e => updateField("district", e.target.value)}
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Pincodes (comma separated)
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 placeholder="400001, 400002"
//                 value={form.pincodes}
//                 onChange={e => updateField("pincodes", e.target.value)}
//               />
//             </div>
//           </div>
//         </div>

//         {/* Actions */}
//         <div className="flex justify-end gap-3">
//           <button
//             type="button"
//             className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50"
//             onClick={() => navigate("/allemployees")}
//           >
//             Cancel
//           </button>
//           <button
//             type="submit"
//             className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary-dark"
//           >
//             Create Employee
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// }



// import { useEffect, useState } from "react";

// const HANDLES = ["AGENT", "RETAILER", "END_USER"];

// export default function EmployeeForm({ managers = [], onSubmit }) {
//   const [categories, setCategories] = useState([]);
//   const [loadingCategories, setLoadingCategories] = useState(false);
//   const [form, setForm] = useState({
//     firstName: "",
//     middleName: "",
//     lastName: "",
//     personalPhone: "",
//     officialPhone: "",
//     officialEmail: "",
//     employeeId: "",
//     category: [],
//     typeOfUser: "SALES",
//     handle: [],
//     reportsToId: "",
//     area: "",
//     state: "",
//     district: "",
//     pincodes: "",
//     position: "EXECUTIVE"
//   });

//   useEffect(() => {
//     async function fetchCategories() {
//       try {
//         setLoadingCategories(true);
//         const res = await fetch("https://goelectronix.in/api/v1/warranty/get-categories");
//         const data = await res.json();
//         if (data) {
//           const active = data.filter(cat => cat.status === 1);
//           setCategories(active);
//         }
//       } catch (error) {
//         console.error("Category fetch error", error);
//       } finally {
//         setLoadingCategories(false);
//       }
//     }
//     fetchCategories();
//   }, []);

//   function updateField(name, value) {
//     setForm(prev => ({ ...prev, [name]: value }));
//   }

//   function toggleMultiSelect(name, value) {
//     setForm(prev => {
//       const list = new Set(prev[name]);
//       list.has(value) ? list.delete(value) : list.add(value);
//       return { ...prev, [name]: [...list] };
//     });
//   }

//   async function handleSubmit(e) {
//     e.preventDefault();
//     try {
//       const authUserString = localStorage.getItem("authUserData");
//       const authUser = authUserString ? JSON.parse(authUserString) : null;
//       const companyId = authUser?.id || 1;

//       const payload = {
//         company_id: companyId,
//         first_name: form.firstName,
//         middle_name: form.middleName,
//         last_name: form.lastName,
//         personal_phone: form.personalPhone,
//         official_phone: form.officialPhone,
//         official_email: form.officialEmail,
//         employee_id: form.employeeId,

//         type_of_user: form.typeOfUser,
//         position: form.position,
//         reports_to: Number(form.reportsToId),

//         categories: form.category,
//         handle: form.handle,

//         pincodes: form.pincodes
//           ? form.pincodes.split(",").map(p => p.trim())
//           : [],

//         state: form.state,
//         district: form.district,

//         password: "123456" // default password
//       };

//       console.log("📌 API Payload:", payload);

//       const response = await fetch(
//         "${BASE_URL}/company/employee/store",
//         {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify(payload),
//         }
//       );

//       const result = await response.json();
//       console.log("📌 API Response:", result);

//       if (result.status === true) {
//         alert("Employee created successfully!");
//         if (onSubmit) onSubmit(form);
//       } else {
//         alert(result.message || "Failed to create employee.");
//       }

//     } catch (error) {
//       console.error("Submit Error:", error);
//       alert("Something went wrong! Try again.");
//     }
//   }

//   return (
//     <div className="mx-auto bg-white shadow-md rounded-2xl p-4 m-4">
//       <h2 className="text-2xl font-semibold text-primary mb-4">
//         Onboard Employee
//       </h2>

//       <form onSubmit={handleSubmit} className="space-y-6">
//         {/* Personal Info */}
//         <div>
//           <h3 className="text-lg font-semibold mb-1 text-slate-800">
//             Personal Information
//           </h3>
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 First Name
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.firstName}
//                 required
//                 onChange={e => updateField("firstName", e.target.value)}
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Middle Name
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.middleName}
//                 onChange={e => updateField("middleName", e.target.value)}
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Last Name
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.lastName}
//                 required
//                 onChange={e => updateField("lastName", e.target.value)}
//               />
//             </div>
//           </div>
//         </div>

//         {/* Contact Info */}
//         <div>
//           <h3 className="text-lg font-semibold mb-1 text-slate-800">
//             Contact Details
//           </h3>
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Personal Phone
//               </label>
//               <input
//                 type="tel"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.personalPhone}
//                 onChange={e => updateField("personalPhone", e.target.value)}
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Official Phone
//               </label>
//               <input
//                 type="tel"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.officialPhone}
//                 onChange={e => updateField("officialPhone", e.target.value)}
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Official Email
//               </label>
//               <input
//                 type="email"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.officialEmail}
//                 onChange={e => updateField("officialEmail", e.target.value)}
//               />
//             </div>
//           </div>
//         </div>

//         {/* Employee Info */}
//         <div>
//           <h3 className="text-lg font-semibold mb-1 text-slate-800">
//             Employee Details
//           </h3>
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Employee ID
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 required
//                 value={form.employeeId}
//                 onChange={e => updateField("employeeId", e.target.value)}
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Type of User
//               </label>
//               <select
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.typeOfUser}
//                 onChange={e => updateField("typeOfUser", e.target.value)}
//               >
//                 <option value="SALES">Sales</option>
//                 <option value="OPERATIONS">Operations</option>
//               </select>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Position
//               </label>
//               <select
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.position}
//                 onChange={e => updateField("position", e.target.value)}
//               >
//                 <option value="MANAGER">Manager</option>
//                 <option value="EXECUTIVE">Executive</option>
//               </select>
//             </div>
//           </div>

//           <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
//             {/* Category */}
//             <div>
//               <label className="block text-sm font-medium text-slate-700 mb-1">
//                 Category
//               </label>

//               {loadingCategories ? (
//                 <p className="text-xs text-slate-500">Loading categories...</p>
//               ) : (
//                 <div className="flex flex-wrap gap-2">
//                   {categories.map(cat => {
//                     const active = form.category.includes(cat.name);
//                     return (
//                       <button
//                         key={cat.id}
//                         type="button"
//                         onClick={() => toggleMultiSelect("category", cat.name)}
//                         className={`px-3 py-1 rounded-full text-xs border ${
//                           active
//                             ? "bg-primary text-white border-primary"
//                             : "bg-slate-50 text-slate-700 border-slate-300"
//                         }`}
//                       >
//                         {cat.name}
//                       </button>
//                     );
//                   })}
//                 </div>
//               )}
//             </div>

//             {/* Handle */}
//             <div>
//               <label className="block text-sm font-medium text-slate-700 mb-1">
//                 Handle
//               </label>
//               <div className="flex flex-wrap gap-2">
//                 {HANDLES.map(h => {
//                   const active = form.handle.includes(h);
//                   return (
//                     <button
//                       key={h}
//                       type="button"
//                       onClick={() => toggleMultiSelect("handle", h)}
//                       className={`px-3 py-1 rounded-full text-xs border ${
//                         active
//                           ? "bg-primary text-white border-primary"
//                           : "bg-slate-50 text-slate-700 border-slate-300"
//                       }`}
//                     >
//                       {h}
//                     </button>
//                   );
//                 })}
//               </div>
//             </div>

//             {/* Reports To */}
//             <div>
//               <label className="block text-sm font-medium text-slate-700 mb-1">
//                 Reports To
//               </label>
//               <select
//                 className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.reportsToId}
//                 onChange={e => updateField("reportsToId", e.target.value)}
//               >
//                 <option value="">Select Manager</option>
//                 {managers.map(m => (
//                   <option key={m.id} value={m.id}>
//                     {m.firstName} {m.lastName} ({m.employeeId})
//                   </option>
//                 ))}
//               </select>
//             </div>
//           </div>
//         </div>

//         {/* Location */}
//         <div>
//           <h3 className="text-lg font-semibold mb-2 text-slate-800">
//             Location Assignment
//           </h3>

//           <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Area
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm 
//                           focus:outline-none focus:ring-2 focus:ring-primary"
//                 placeholder="Enter Area"
//                 value={form.area}
//                 onChange={(e) => updateField("area", e.target.value)}
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 State
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm 
//                           focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.state}
//                 onChange={e => updateField("state", e.target.value)}
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 District
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm 
//                           focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.district}
//                 onChange={e => updateField("district", e.target.value)}
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Pincodes (comma separated)
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm 
//                           focus:outline-none focus:ring-2 focus:ring-primary"
//                 placeholder="400001, 400002"
//                 value={form.pincodes}
//                 onChange={e => updateField("pincodes", e.target.value)}
//               />
//             </div>
//           </div>
//         </div>

//         {/* Actions */}
//         <div className="flex justify-end gap-3">
//           <button
//             type="button"
//             className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50"
//           >
//             Cancel
//           </button>
//           <button
//             type="submit"
//             className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary-dark"
//           >
//             Create Employee
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// }




// import { useEffect, useState } from "react";

// const CATEGORIES = ["TV", "REF", "MOBILE", "TABLET", "IRON"];
// const HANDLES = ["AGENT", "RETAILER", "END_USER"];

// export default function EmployeeForm({ managers = [], onSubmit }) {

//   const [categories, setCategories] = useState([]); // API categories
//   const [loadingCategories, setLoadingCategories] = useState(false);
//   const [form, setForm] = useState({
//     firstName: "",
//     middleName: "",
//     lastName: "",
//     personalPhone: "",
//     officialPhone: "",
//     officialEmail: "",
//     employeeId: "",
//     category: [],
//     typeOfUser: "SALES",
//     handle: [],
//     reportsToId: "",
//     area: "",
//     state: "",
//     district: "",
//     pincodes: "",
//     position: "EXECUTIVE"
//   });

//   useEffect(() => {
//     async function fetchCategories() {
//       try {
//         setLoadingCategories(true);
//         const res = await fetch("https://goelectronix.in/api/v1/warranty/get-categories");
//         const data = await res.json();

//         if (data) {
//           const active = data.filter(cat => cat.status === 1);
//           setCategories(active);
//         }
//       } catch (error) {
//         console.error("Category fetch error", error);
//       } finally {
//         setLoadingCategories(false);
//       }
//     }

//     fetchCategories();
//   }, []);

//   function updateField(name, value) {
//     setForm(prev => ({ ...prev, [name]: value }));
//   }

//   function toggleMultiSelect(name, value) {
//     setForm(prev => {
//       const list = new Set(prev[name]);
//       if (list.has(value)) list.delete(value);
//       else list.add(value);
//       return { ...prev, [name]: Array.from(list) };
//     });
//   }

//   function handleSubmit(e) {
//     e.preventDefault();
//     if (onSubmit) onSubmit(form);
//     // later: call API here
//   }

//   return (
//     <div className="mx-auto bg-white shadow-md rounded-2xl p-4 m-4">
//       <h2 className="text-2xl font-semibold text-primary mb-4">
//         Onboard Employee
//       </h2>

//       <form onSubmit={handleSubmit} className="space-y-6">
//         {/* Personal Info */}
//         <div>
//           <h3 className="text-lg font-semibold mb-1 text-slate-800">
//             Personal Information
//           </h3>
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 First Name
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.firstName}
//                 onChange={e => updateField("firstName", e.target.value)}
//                 required
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Middle Name
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.middleName}
//                 onChange={e => updateField("middleName", e.target.value)}
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Last Name
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.lastName}
//                 onChange={e => updateField("lastName", e.target.value)}
//                 required
//               />
//             </div>
//           </div>
//         </div>

//         {/* Contact Info */}
//         <div>
//           <h3 className="text-lg font-semibold mb-1 text-slate-800">
//             Contact Details
//           </h3>
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Personal Phone
//               </label>
//               <input
//                 type="tel"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.personalPhone}
//                 onChange={e => updateField("personalPhone", e.target.value)}
//               />
//             </div>
//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Official Phone
//               </label>
//               <input
//                 type="tel"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.officialPhone}
//                 onChange={e => updateField("officialPhone", e.target.value)}
//               />
//             </div>
//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Official Email
//               </label>
//               <input
//                 type="email"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.officialEmail}
//                 onChange={e => updateField("officialEmail", e.target.value)}
//               />
//             </div>
//           </div>
//         </div>

//         {/* Employee Info */}
//         <div>
//           <h3 className="text-lg font-semibold mb-1 text-slate-800">
//             Employee Details
//           </h3>
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Employee ID
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.employeeId}
//                 onChange={e => updateField("employeeId", e.target.value)}
//                 required
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Type of User
//               </label>
//               <select
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.typeOfUser}
//                 onChange={e => updateField("typeOfUser", e.target.value)}
//               >
//                 <option value="SALES">Sales</option>
//                 <option value="OPERATIONS">Operations</option>
//               </select>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Position
//               </label>
//               <select
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.position}
//                 onChange={e => updateField("position", e.target.value)}
//               >
//                 <option value="MANAGER">Manager</option>
//                 <option value="EXECUTIVE">Executive</option>
//               </select>
//             </div>
//           </div>

//           <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
//             {/* Categories */}
          //   <div>
          //   <label className="block text-sm font-medium text-slate-700 mb-1">
          //     Category
          //   </label>

          //   {loadingCategories ? (
          //     <p className="text-xs text-slate-500">Loading categories...</p>
          //   ) : (
          //     <div className="flex flex-wrap gap-2">
          //       {categories.map(cat => {
          //         const active = form.category.includes(cat.name);
          //         return (
          //           <button
          //             key={cat.id}
          //             type="button"
          //             onClick={() => toggleMultiSelect("category", cat.name)}
          //             className={`px-3 py-1 rounded-full text-xs border ${
          //               active
          //                 ? "bg-primary text-white border-primary"
          //                 : "bg-slate-50 text-slate-700 border-slate-300"
          //             }`}
          //           >
          //             {cat.name}
          //           </button>
          //         );
          //       })}
          //     </div>
          //   )}
          // </div>

//             {/* Handle */}
//             <div>
//               <label className="block text-sm font-medium text-slate-700 mb-1">
//                 Handle
//               </label>
//               <div className="flex flex-wrap gap-2">
//                 {HANDLES.map(h => {
//                   const active = form.handle.includes(h);
//                   return (
//                     <button
//                       key={h}
//                       type="button"
//                       onClick={() => toggleMultiSelect("handle", h)}
//                       className={`px-3 py-1 rounded-full text-xs border ${
//                         active
//                           ? "bg-primary text-white border-primary"
//                           : "bg-slate-50 text-slate-700 border-slate-300"
//                       }`}
//                     >
//                       {h}
//                     </button>
//                   );
//                 })}
//               </div>
//             </div>

//             {/* Reports To */}
//             <div>
//               <label className="block text-sm font-medium text-slate-700 mb-1">
//                 Reports To
//               </label>
//               <select
//                 className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.reportsToId}
//                 onChange={e => updateField("reportsToId", e.target.value)}
//               >
//                 <option value="">Select Manager</option>
//                 {managers.map(m => (
//                   <option key={m.id} value={m.id}>
//                     {m.firstName} {m.lastName} ({m.employeeId})
//                   </option>
//                 ))}
//               </select>
//             </div>
//           </div>

//         </div>

//         {/* Location */}
//         <div>
//           <h3 className="text-lg font-semibold mb-2 text-slate-800">
//             Location Assignment
//           </h3>

//           <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            
//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Area
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm 
//                           focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.area}
//                 onChange={(e) => updateField("area", e.target.value)}
//                 placeholder="Enter Area"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 State
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm 
//                           focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.state}
//                 onChange={e => updateField("state", e.target.value)}
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 District
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm 
//                           focus:outline-none focus:ring-2 focus:ring-primary"
//                 value={form.district}
//                 onChange={e => updateField("district", e.target.value)}
//               />
//             </div>

//             {/* 👉 NEW AREA FIELD */}
            

//             <div>
//               <label className="block text-sm font-medium text-slate-700">
//                 Pincodes (comma separated)
//               </label>
//               <input
//                 type="text"
//                 className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm 
//                           focus:outline-none focus:ring-2 focus:ring-primary"
//                 placeholder="400001, 400002"
//                 value={form.pincodes}
//                 onChange={e => updateField("pincodes", e.target.value)}
//               />
//             </div>

//           </div>
//         </div>


//         {/* Actions */}
//         <div className="flex justify-end gap-3">
//           <button
//             type="button"
//             className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50"
//           >
//             Cancel
//           </button>
//           <button
//             type="submit"
//             className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary-dark"
//           >
//             Create Employee
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// }



// import React, { useState, useEffect } from "react";
// import axios from "axios";

// import Swal from "sweetalert2";
// import { useNavigate } from "react-router-dom";
// import BlueButton from "../../../Component/Common/BlueButton";

// export default function RetailerForm({ onCancel, onSubmit }) {
//   const navigate = useNavigate();

//   const [form, setForm] = useState({
//     business_name: "",
//     owner_name: "",
//     mobile: "",
//     email: "",
//     password: "",
//     taluka: "",
//     pincode: "",
//     gst: "",
//     pan: "",
//     role: "5", // Default Retailer
//     document: null,
//     logo: null,
//     state: "",
//     district: "",
//     is_active: 1,
//     company_id: "",
//     company_name: "",
//     dist_id: "",
//     dist_name: "",
//     senior_id: "",
//   });

//   const [errors, setErrors] = useState({});
//   const [loadingPin, setLoadingPin] = useState(false);

//   // 🔹 Load session values
//   useEffect(() => {
//     const authUserString = localStorage.getItem("authUserData");
//     const authUser = JSON.parse(authUserString);
//     const loginUserID = authUser?.id;
//     const CompanyID = authUser?.company_id;
//     const CompanyName = authUser?.company_name;
//     const LoginUserName = authUser?.business_name;

//     setForm((prev) => ({
//       ...prev,
//       company_id: CompanyID || "",
//       company_name: CompanyName || "",
//       dist_id: loginUserID || "",
//       dist_name: LoginUserName || "",
//       senior_id: loginUserID || "",
//     }));
//   }, []);

//   // 🔹 Handle Input Change
//   const handleChange = (e) => {
//     const { name, value, files } = e.target || {};

//     if (files) {
//       setForm((prev) => ({ ...prev, [name]: files[0] }));
//     } else {
//       setForm((prev) => ({ ...prev, [name]: value }));
//     }

//     setErrors((prev) => ({ ...prev, [name]: "" }));

//     // 🔹 Auto fetch state/district using Pincode
//     if (name === "pincode") {
//       const pin = value.trim();
//       if (pin.length === 6 && /^\d{6}$/.test(pin)) {
//         fetchStateAndDistrict(pin);
//       } else if (pin.length > 0 && pin.length !== 6) {
//         setErrors((prev) => ({
//           ...prev,
//           pincode: "Please enter a valid 6-digit pincode.",
//         }));
//         setForm((prev) => ({ ...prev, state: "", district: "" }));
//       }
//     }
//   };

//   // 🔹 Fetch state & district from Pincode API
//   const fetchStateAndDistrict = async (pincode) => {
//     try {
//       setLoadingPin(true);
//       const res = await axios.get(
//         `https://goelectronix.in/public/api/v1/pincode/${pincode}`
//       );

//       if (res.data?.success) {
//         const { state, district } = res.data;
//         setForm((prev) => ({
//           ...prev,
//           state: state || "",
//           district: district || "",
//         }));
//         setErrors((prev) => ({ ...prev, pincode: "" }));
//       } else {
//         setErrors((prev) => ({
//           ...prev,
//           pincode: "Please type correct pincode.",
//         }));
//         setForm((prev) => ({ ...prev, state: "", district: "" }));
//       }
//     } catch (error) {
//       console.error("Error fetching pincode info:", error);
//       setErrors((prev) => ({
//         ...prev,
//         pincode: "Please type correct pincode.",
//       }));
//       setForm((prev) => ({ ...prev, state: "", district: "" }));
//     } finally {
//       setLoadingPin(false);
//     }
//   };

//   // 🔹 Validation logic
//   const validateForm = () => {
//     const newErrors = {};

//     if (!form.business_name.trim())
//       newErrors.business_name = "Business name is required.";
//     if (!form.owner_name.trim()) newErrors.owner_name = "Owner name is required.";
//     if (!form.mobile.trim()) newErrors.mobile = "Mobile number is required.";
//     else if (!/^[6-9]\d{9}$/.test(form.mobile))
//       newErrors.mobile = "Enter a valid 10-digit mobile number.";
//     if (!form.email.trim()) newErrors.email = "Email is required.";
//     else if (!/\S+@\S+\.\S+/.test(form.email))
//       newErrors.email = "Enter a valid email address.";
//     if (!form.password.trim()) newErrors.password = "Password is required.";
//     else if (form.password.length < 6)
//       newErrors.password = "Password must be at least 6 characters.";
//     if (!form.pan.trim()) newErrors.pan = "PAN number is required.";
//     else if (!/[A-Z]{5}[0-9]{4}[A-Z]{1}/.test(form.pan))
//       newErrors.pan = "Enter a valid PAN number.";
//     if (!form.taluka.trim()) newErrors.taluka = "Taluka is required.";
//     if (!form.state.trim()) newErrors.state = "State is required.";
//     if (!form.district.trim()) newErrors.district = "District is required.";
//     if (!form.pincode.trim()) newErrors.pincode = "Pincode is required.";
//     else if (!/^\d{6}$/.test(form.pincode))
//       newErrors.pincode = "Enter a valid 6-digit pincode.";

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   // 🔹 Submit Handler
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!validateForm()) return;

//     try {
//       const formData = new FormData();
//       Object.keys(form).forEach((key) => {
//         if (form[key] !== null && form[key] !== undefined) {
//           formData.append(key, form[key]);
//         }
//       });

//       await axios.post("https://goelectronix.in/api/v1/org_user", formData, {
//         headers: { "Content-Type": "multipart/form-data" },
//       });

//       Swal.fire({
//         title: "Success",
//         text: "Retailer saved successfully!",
//         icon: "success",
//         confirmButtonColor: "#2563eb",
//       }).then(() => navigate("/allretailers"));

//       onSubmit();
//     } catch (err) {
//       console.error("Error saving retailer:", err);
//       Swal.fire({
//         title: "Error",
//         text: "Something went wrong while saving retailer.",
//         icon: "error",
//         confirmButtonColor: "#dc2626",
//       });
//     }
//   };

//   return (
//     <form onSubmit={handleSubmit} className="space-y-3">
//       <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
//         {/* Business Name */}
//         <div>
//           <input
//             name="business_name"
//             value={form.business_name}
//             onChange={handleChange}
//             placeholder="Enter business name"
//             className="rounded-lg border px-3 py-2 text-sm w-full"
//           />
//           {errors.business_name && (
//             <p className="text-red-500 text-xs">{errors.business_name}</p>
//           )}
//         </div>

//         {/* Owner Name */}
//         <div>
//           <input
//             name="owner_name"
//             value={form.owner_name}
//             onChange={handleChange}
//             placeholder="Enter owner name"
//             className="rounded-lg border px-3 py-2 text-sm w-full"
//           />
//           {errors.owner_name && (
//             <p className="text-red-500 text-xs">{errors.owner_name}</p>
//           )}
//         </div>

//         {/* Mobile */}
//         <div>
//           <input
//             name="mobile"
//             value={form.mobile}
//             onChange={handleChange}
//             placeholder="Enter phone no."
//             className="rounded-lg border px-3 py-2 text-sm w-full"
//           />
//           {errors.mobile && (
//             <p className="text-red-500 text-xs">{errors.mobile}</p>
//           )}
//         </div>

//         {/* Email */}
//         <div>
//           <input
//             name="email"
//             value={form.email}
//             onChange={handleChange}
//             placeholder="Enter email"
//             className="rounded-lg border px-3 py-2 text-sm w-full"
//           />
//           {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
//         </div>

//         {/* Password */}
//         <div>
//           <input
//             name="password"
//             type="password"
//             value={form.password}
//             onChange={handleChange}
//             placeholder="Enter password"
//             className="rounded-lg border px-3 py-2 text-sm w-full"
//           />
//           {errors.password && (
//             <p className="text-red-500 text-xs">{errors.password}</p>
//           )}
//         </div>

//         {/* GST (optional) */}
//         <input
//           name="gst"
//           value={form.gst}
//           onChange={handleChange}
//           placeholder="Enter GST No. (optional)"
//           className="rounded-lg border px-3 py-2 text-sm w-full"
//         />

//         {/* PAN */}
//         <div>
//           <input
//             name="pan"
//             value={form.pan}
//             onChange={handleChange}
//             placeholder="Enter PAN No."
//             className="rounded-lg border px-3 py-2 text-sm w-full"
//           />
//           {errors.pan && <p className="text-red-500 text-xs">{errors.pan}</p>}
//         </div>

//         {/* Pincode */}
//         <div className="relative">
//           <input
//             name="pincode"
//             value={form.pincode}
//             onChange={handleChange}
//             placeholder="Enter Pin Code"
//             className="rounded-lg border px-3 py-2 text-sm w-full"
//           />
//           {loadingPin && (
//             <span className="absolute right-3 top-2.5 text-xs text-gray-500">
//               Loading...
//             </span>
//           )}
//           {errors.pincode && (
//             <p className="text-red-500 text-xs">{errors.pincode}</p>
//           )}
//         </div>

//         {/* Taluka */}
//         <div>
//           <input
//             name="taluka"
//             value={form.taluka}
//             onChange={handleChange}
//             placeholder="Enter Taluka"
//             className="rounded-lg border px-3 py-2 text-sm w-full"
//           />
//           {errors.taluka && (
//             <p className="text-red-500 text-xs">{errors.taluka}</p>
//           )}
//         </div>

//         {/* State */}
//         <div>
//           <input
//             name="state"
//             value={form.state}
//             readOnly
//             placeholder="Auto-filled State"
//             className="rounded-lg border px-3 py-2 text-sm w-full bg-gray-100"
//           />
//           {errors.state && (
//             <p className="text-red-500 text-xs">{errors.state}</p>
//           )}
//         </div>

//         {/* District */}
//         <div>
//           <input
//             name="district"
//             value={form.district}
//             readOnly
//             placeholder="Auto-filled District"
//             className="rounded-lg border px-3 py-2 text-sm w-full bg-gray-100"
//           />
//           {errors.district && (
//             <p className="text-red-500 text-xs">{errors.district}</p>
//           )}
//         </div>

//         {/* Document */}
//         <div className="flex flex-col">
//           <label className="text-xs mb-1">Document (optional)</label>
//           <input
//             type="file"
//             name="document"
//             onChange={handleChange}
//             className="text-sm"
//           />
//         </div>

//         {/* Logo */}
//         <div className="flex flex-col">
//           <label className="text-xs mb-1">Logo (optional)</label>
//           <input
//             type="file"
//             name="logo"
//             onChange={handleChange}
//             className="text-sm"
//           />
//         </div>
//       </div>

//       {/* Buttons */}
//       <div className="flex justify-end gap-2 mt-2">
//         <BlueButton type="button" onClick={onCancel}>
//           Cancel
//         </BlueButton>
//         <BlueButton type="submit">Save Retailer</BlueButton>
//       </div>
//     </form>
//   );
// }
